import { Controller } from "@hotwired/stimulus"
import { loadMapEngine, getPixelRatio, setupDprResize, quietMarker, LOCAL_CJK_FONT } from "./map_engine"
import { MapTelemetryClient } from "../lib/map_telemetry_client"
import { prefersReducedMotion, motionLite } from "../lib/motion"
import { MAP_INTERACTION_EVENT } from "../lib/prompt_sequence"
import { lockScroll, unlockScroll } from "../lib/scroll_lock"

// Viewport geojson (villages/listings) bbox snapping. Raw float bounds never repeat, so the public
// 5-min HTTP cache on those endpoints never hits. Snapping the bbox OUTWARD to a ~550m grid (floor min,
// ceil max) makes adjacent viewports in the same grid cell share one URL (cache hits) while still fully
// covering the visible viewport (no edge feature clipped).
const VIEWPORT_BBOX_GRID_DEG = 0.005
const VIEWPORT_BBOX_DECIMALS = 4

// Persistent-map keep-alive singleton. The live MapLibre instance is held here (module scope, NOT a class
// static — esbuild's transpiled `this.constructor` does not reliably alias one class object across Stimulus
// re-instantiations) so it survives the controller disconnect/reconnect churn of every Turbo navigation and
// is re-adopted instead of rebuilt (a fresh WebGL context + style/glyph load per /home visit is the freeze).
const mapKeepAlive = { instance: null }

// The district sheet lives on the permanent map shell, so it stays open across navigations. Coming back from a
// listing's detail page keeps it (the user is working through that list); any other page in between closes it.
const LISTING_DETAIL_PATH = /^\/listings\/\d+(\/|$)/
const sheetMemory = { keep: true }

// Arrival fountain — expression Twemoji codes sprayed up from a deep-linked pin (see spawnArrivalEmotes).
const ARRIVAL_EMOTE_CODES = [ "1f600", "1f604", "1f609", "1f60d", "1f62e", "1f929", "1f973" ]
const ARRIVAL_FOUNTAIN_MS = 4500 // a short celebratory burst on arrival, then the map is left alone
const ARRIVAL_WAVE_MS = 680        // gap between waves (a few droplets each)
// Product arrival (/home?product=): framing keeps both points clear of the search bar + chips (top), the legend
// and tab bar (bottom), the side buttons, and the price pin's own card (~70px tall, ~200px wide above its point)
const PRODUCT_ARRIVAL_PADDING = { top: 240, bottom: 200, left: 110, right: 110 }
const PRODUCT_ARRIVAL_FLIGHT_MS = 1100
// Auto tilt at street zoom stays gentle so labels and cards stay legible over the 3D massing
const MAX_AUTO_PITCH = 35

/**
 * Region Map Controller
 *
 * Self-hosted MapLibre GL JS + PMTiles interactive map for Taiwan real estate discovery.
 * No API key, no third-party tile billing — style + tiles served same-origin.
 * Displays district polygons with price-based choropleth coloring.
 * Dynamically loads village-level polygons at high zoom levels (>13).
 *
 * Features:
 * - District polygon rendering with price visualization (zoom ≤ 13)
 * - Village polygon rendering at high zoom (zoom > 13)
 * - Click to select district/village and load listings in sidebar
 * - Listing type toggle (rent/sale)
 * - Search & filter integration
 * - 3-tier zoom strategy with dynamic pitch for tile optimization
 *
 * Usage:
 * <div data-controller="region-map"
 *      data-region-map-api-key-value="..."
 *      data-region-map-districts-url-value="/api/v1/geojson/districts"
 *      data-region-map-villages-url-value="/api/v1/geojson/villages"
 *      data-region-map-listings-url-value="/districts/:id/listings">
 */
export default class extends Controller {
  static targets = [
    "container",
    "sidebar",
    "sidebarBackdrop",
    "loader",
    "listingTypeSelect",
    "swipeHandle",
    // Legend targets (dual render for rent/sale toggle)
    "rentLegend",
    "saleLegend",
    // Filter modal targets
    "filterModal",
    "filterModalContent",
    "priceMinDisplay",
    "priceMaxDisplay",
    // Slim-sheet structure
    "moreFilters",
    "moreFiltersToggle",
    "moreFiltersChevron",
    "applyCount",
    // Inline property-type bar (controller-managed region)
    "propertyTypeBar",
    // Search-header active-filter badge (kept live as filters commit)
    "filterBadge",
    "filterButton",
    // Price-intelligence panel
    "pricePanel",
    "pricePanelContent",
    "pricePanelFrame",
    "pricePanelSkeleton",
    // Feature filter targets
    "petAllowedCheckbox",
    "furnishedCheckbox",
    "hasElevatorCheckbox"
  ]

  static values = {
    styleUrl: String,
    center: { type: Array, default: [121.5654, 25.0330] },
    explicitCenter: { type: Boolean, default: false },
    zoom: { type: Number, default: 12 },
    districtsUrl: String,
    villagesUrl: String,
    listingsUrl: String,
    listingsGeojsonUrl: String,
    markerZoom: { type: Number, default: 16 },
    unitPing: String,
    unitCount: String,
    viewDetailLabel: String,
    pricePanelUrl: String,
    listingType: { type: String, default: "rent" },
    statsVersion: { type: String, default: "" },
    villagesStatsVersion: { type: String, default: "" },
    filters: { type: Object, default: {} },
    priceUnlimited: String,
    priceUnitWan: String,
    priceMonthlySuffix: String,
    applyLabel: String,
    applyCountTemplate: String,
    filterLabel: String,
    filterCountTemplate: String,
    // L3 behavioral collection. Off unless the signed-in user opted in;
    // when false the controller NEVER beacons (client-side fail-closed; server re-checks too).
    l3Consent: { type: Boolean, default: false },
    beaconUrl: { type: String, default: "" }
  }

  async connect() {
    this.interactionNoted = false
    this.selectedDistrictId = null
    this.hoveredDistrictId = null
    this.hoveredVillageId = null
    this.pendingFilters = { ...this.filtersValue }
    this.villageLayersLoaded = false
    this.villageLoadAbortController = null

    // Store event handler references for cleanup
    this.boundEventHandlers = {}

    // Touch gesture state for bottom sheet swipe
    this.touchStartY = null
    this.isDragging = false
    this.sidebarExpanded = false

    // Cache zoom threshold once (SSOT from BusinessConstants, fallback 13)
    this.villageZoomThreshold = window.BusinessConstants?.RealEstate?.VILLAGE_ZOOM_THRESHOLD ?? 13

    // 3-tier zoom strategy: track last applied tier to prevent redundant transitions
    this.lastAppliedTier = null

    // Village reload debouncing + bounds dedup
    this.villageReloadTimer = null
    this.lastVillageBoundsHash = null

    // Max-zoom tier: 區/里 count bubbles fade out and individual building price pills take over.
    // Fade tuning is SSOT in BusinessConstants::RealEstate (injected via the layout); the literal
    // here is only a fallback when the injection is absent (mirrors villageZoomThreshold).
    const re = window.BusinessConstants?.RealEstate || {}
    this.markerZoomThreshold = this.markerZoomValue || 16
    this.choroplethFadeOpacity = re.CHOROPLETH_FADE_OPACITY ?? 0.1
    this.listingMarkersLoaded = false
    this.listingMarkerAbortController = null
    this.listingMarkerReloadTimer = null
    this.lastMarkerBoundsHash = null
    this.listingPopup = null
    this.mapTelemetry = new MapTelemetryClient({
      isEnabled: () => Boolean(this.l3ConsentValue && this.beaconUrlValue && this.map),
      endpoint: () => this.beaconUrlValue,
      config: () => window.BusinessConstants?.MapEvents || {},
      buildEvent: (eventType, clientSequence) => this.buildMapBeaconEvent(eventType, clientSequence)
    })
    this.boundPageHide = () => this.mapTelemetry.pagehide()
    window.addEventListener("pagehide", this.boundPageHide)

    // iOS WKWebView Cache API detection
    if (!window.caches) {
      console.warn("[RegionMap] Cache API unavailable — HTTP cache will be used as fallback")
    }

    // Register visibilitychange listener once (free GeoJSON cache when tab is hidden)
    if (!this.constructor._visibilityListenerRegistered) {
      const Klass = this.constructor
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) {
          Klass._districtCache = null // ~20MB freed
          // Keep _savedViewport (~100B)
        }
      })
      this.constructor._visibilityListenerRegistered = true
    }

    // Visibility gate: only (re)engage the map when its shell is actually shown (/home). On non-home pages
    // #map-shell-host is display:none, so loading the MapLibre engine, spinning up a WebGL context, or
    // building into a 0×0 hidden container would all be wasted and fragile. Stay dormant — the stashed
    // instance lives on (module singleton) and is re-adopted the moment a /home navigation re-connects us.
    if (this.element.closest("[data-map-shell]")?.dataset.mapShell !== "home") {
      if (!LISTING_DETAIL_PATH.test(window.location.pathname)) sheetMemory.keep = false
      return
    }
    this.restoreSheetForReturn()

    // Android loads every tab's WebView at launch and one renderer thread serves them all: an off-screen map
    // tab waits until it is shown, so its build doesn't stall the visible tab's launch animation.
    if (window.__toas2tTabHidden === true) {
      await new Promise((resolve) => {
        this.boundTabShown = (event) => { if (!event.detail?.hidden) resolve() }
        window.addEventListener("native:tab-visibility", this.boundTabShown)
      })
      window.removeEventListener("native:tab-visibility", this.boundTabShown)
      if (!this.element.isConnected) return
    }

    // Persistent-map keep-alive: the map instance + its layers live on the module singleton across Turbo
    // navigations (the #map-permanent container is preserved by the layout shell). Re-adopt it instead of
    // rebuilding — a fresh WebGL context + style/glyph load on every /home visit was the freeze cause.
    // Rebuild only when no live instance exists, or its container was torn down (e.g. after a full reload).
    const stash = mapKeepAlive.instance
    if (stash && stash.getContainer && document.body.contains(stash.getContainer())) {
      // Re-adopt the live instance — no engine load or rebuild needed.
      this.map = stash
      this.reEngageMap()
    } else {
      // Build path: load the engine (lazy MapLibre + PMTiles import) only when we actually build a map.
      try {
        this.engine = await loadMapEngine()
      } catch (error) {
        console.error("[RegionMap] Failed to load map engine:", error)
        return
      }
      // Guard: element may have disconnected during async import (Turbo navigation)
      if (!this.element.isConnected) return
      if (stash) { try { stash.remove() } catch (_e) { /* already gone */ } mapKeepAlive.instance = null }
      this.initializeMap()
    }
    this.setupDistrictAutocompleteListener()
    this.setupDropdownShownListener()
  }

  // Back on /home: keep an open sheet only when the user went no further than listing detail pages; otherwise
  // close it without the slide (it was left open, not opened now). Either way the controller state matches the DOM.
  restoreSheetForReturn() {
    const keep = sheetMemory.keep
    sheetMemory.keep = true
    if (!this.hasSidebarTarget || this.sidebarTarget.classList.contains("translate-y-full")) return

    if (keep) {
      this.sidebarExpanded = true
      return
    }
    this.sidebarTarget.style.transition = "none"
    this.hideSidebar(false)
    void this.sidebarTarget.offsetHeight
    this.sidebarTarget.style.transition = ""
  }

  // Re-adopt a persisted map after a Turbo navigation re-connected the controller. The map, its WebGL
  // context, style and layers all survive on the static field; we only re-point the instance-level handlers
  // at THIS controller and re-sync the zoom tier. No rebuild (the freeze cost) — that is the whole point.
  reEngageMap() {
    this.attachCoreMapHandlers()
    if (this.map.getLayer("districts-fill")) this.setupMapEventListeners()
    this.villageLayersLoaded = !!this.map.getLayer("villages-fill")
    this.listingMarkersLoaded = !!this.map.getLayer("listing-marker")
    if (this.villageLayersLoaded) this.setupVillageEventListeners()
    if (this.listingMarkersLoaded) this.setupListingMarkerListeners()
    this.map.resize()
    // A deep-linked center (e.g. /home?lat=…&lng=… from a detail-page thumbnail) must win on re-adopt.
    // Read from the LIVE URL, NOT the data attribute: #map-permanent is data-turbo-permanent, so its
    // center/explicit-center values are frozen at first render and never reflect a later deep-link nav.
    const arrival = this.productArrivalFromPage()
    const deepLink = arrival ? null : this.deepLinkCenterFromUrl()
    if (arrival) {
      this.showProductArrival(arrival)
    } else if (deepLink) {
      this.map.jumpTo({ center: deepLink.center, zoom: deepLink.zoom })
      this.spawnArrivalEmotes(deepLink.center) // playful emoji burst at the arrived pin (ARRIVAL_FOUNTAIN_MS)
    }
    // Districts absent (first build had errored / never reached) → load now; else re-sync tier to zoom.
    if (this.map.getLayer("districts-fill")) {
      this.handleZoomChange()
    } else {
      this.loadDistrictPolygons().then(() => this.handleZoomChange()).catch(() => this.handleZoomChange())
    }
  }

  // Deep-link center/zoom from the LIVE URL (?lat&lng&zoom). The #map-permanent host is
  // data-turbo-permanent, so its data-region-map-*-value attributes are frozen at first render and do
  // NOT update on later Turbo navigations — the URL is the only fresh source on re-adopt. Mirrors
  // HomeController#map_center_from_params (finite + inside Taiwan bounds).
  // @return {{center: number[], zoom: number}|null} [lng, lat] + zoom, or null when absent/invalid.
  deepLinkCenterFromUrl() {
    const params = new URLSearchParams(window.location.search)
    const lat = parseFloat(params.get("lat"))
    const lng = parseFloat(params.get("lng"))
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
    const gps = window.BusinessConstants?.GPS || {}
    const inLat = lat >= (gps.TAIWAN_MIN_LAT ?? 21.5) && lat <= (gps.TAIWAN_MAX_LAT ?? 26.5)
    const inLng = lng >= (gps.TAIWAN_MIN_LNG ?? 118.0) && lng <= (gps.TAIWAN_MAX_LNG ?? 122.5)
    if (!inLat || !inLng) return null
    const zoom = parseInt(params.get("zoom"), 10)
    return { center: [ lng, lat ], zoom: Number.isFinite(zoom) ? zoom : this.zoomValue }
  }

  // Arrival fountain: expression Twemoji spray up from the deep-linked pin, arc out + fall + fade,
  // emitted in waves for ARRIVAL_FOUNTAIN_MS ("분수처럼"). Self-hosted Twemoji <img> + GPU CSS motion (.map-emote);
  // a MapLibre Marker anchors the spray to the geo point so it tracks pan/zoom. Each droplet removes
  // itself on animationend. Skipped under prefers-reduced-motion.
  async spawnArrivalEmotes(center) {
    if (prefersReducedMotion()) return
    this.clearArrivalEmotes()
    const engine = this.engine || (await loadMapEngine().catch(() => null))
    if (!engine || !this.map) return

    const el = document.createElement("div")
    el.setAttribute("aria-hidden", "true")
    el.style.pointerEvents = "none"
    this.arrivalMarker = new engine.Marker({ element: el, anchor: "center" }).setLngLat(center).addTo(this.map)

    const rand = (min, max) => min + Math.random() * (max - min)
    const emit = () => {
      if (!this.arrivalMarker) return
      const n = 2 + Math.floor(Math.random() * 2) // 2–3 droplets per wave
      for (let i = 0; i < n; i++) {
        const code = ARRIVAL_EMOTE_CODES[Math.floor(Math.random() * ARRIVAL_EMOTE_CODES.length)]
        const img = document.createElement("img")
        img.src = `/twemoji/${code}.svg`
        img.alt = ""
        img.className = "map-emote"
        img.style.setProperty("--dx", `${Math.round(rand(-72, 72))}px`)     // horizontal spread
        img.style.setProperty("--peak", `${Math.round(rand(-136, -90))}px`) // arc apex (up = negative)
        img.style.setProperty("--rot", `${Math.round(rand(-26, 26))}deg`)
        img.style.animationDuration = `${rand(2.6, 3.5).toFixed(2)}s`
        img.addEventListener("animationend", () => img.remove(), { once: true })
        el.appendChild(img)
      }
    }
    emit()
    this.arrivalInterval = setInterval(emit, ARRIVAL_WAVE_MS)
    // Stop emitting after ~30s; remove the marker once the last droplets finish (~3.6s later).
    this.arrivalStopTimer = setTimeout(() => {
      if (this.arrivalInterval) { clearInterval(this.arrivalInterval); this.arrivalInterval = null }
      this.arrivalCleanupTimer = setTimeout(() => this.clearArrivalEmotes(), 3600)
    }, ARRIVAL_FOUNTAIN_MS)
  }

  // /home?product= payload from home/_product_arrival (HomeHelper#product_arrival_payload). It lives outside the
  // permanent map host, so unlike the frozen data-region-map-* values it is fresh on every visit.
  // @return {{item: number[], zoom: number, viewer: number[]|null, title: string, price: string, free: boolean,
  //   distance: string|null, thumb: string|null, url: string, label: string}|null}
  productArrivalFromPage() {
    const el = document.querySelector("[data-map-arrival]")
    if (!el) return null
    try {
      const arrival = JSON.parse(el.dataset.mapArrival)
      return Array.isArray(arrival?.item) ? arrival : null
    } catch (_e) {
      return null
    }
  }

  // Product arrival: frame the viewer and the item in one camera move, radar rings spread from the viewer, then
  // the item's price pin pops. DOM markers with CSS transform/opacity only (components/map_arrival.css); no
  // layer or source changes. Motion-lite / reduced motion: jump, and both markers appear at rest.
  async showProductArrival(arrival) {
    this.clearArrivalEmotes()
    const engine = this.engine || (await loadMapEngine().catch(() => null))
    if (!engine || !this.map) return

    const still = motionLite()
    if (arrival.viewer) {
      const bounds = new engine.maplibregl.LngLatBounds(arrival.viewer, arrival.viewer).extend(arrival.item)
      this.map.fitBounds(bounds, { padding: PRODUCT_ARRIVAL_PADDING, maxZoom: arrival.zoom, duration: still ? 0 : PRODUCT_ARRIVAL_FLIGHT_MS })
      this.arrivalRadar = quietMarker(new engine.Marker({ element: this.buildArrivalRadar(still), anchor: "center" })
        .setLngLat(arrival.viewer).addTo(this.map))
    } else {
      this.map.jumpTo({ center: arrival.item, zoom: arrival.zoom })
    }
    // The pin's inner link is the control; the marker root only positions it
    this.arrivalPin = quietMarker(new engine.Marker({ element: this.buildArrivalPin(arrival, still), anchor: "bottom", offset: [ 0, 7 ] })
      .setLngLat(arrival.item).addTo(this.map), { hidden: false })
  }

  // Marker roots are positioned by MapLibre's transform, so the animated parts are children.
  buildArrivalRadar(still) {
    const host = document.createElement("div")
    host.setAttribute("aria-hidden", "true")
    host.innerHTML = `<div class="map-arrival-radar${still ? " is-still" : ""}"><span></span><span></span><span></span><i></i></div>`
    return host
  }

  buildArrivalPin(arrival, still) {
    const host = document.createElement("div")
    const pin = document.createElement("a")
    pin.className = `map-arrival-pin${still ? " is-still" : ""}`
    pin.href = arrival.url
    pin.setAttribute("aria-label", arrival.label)

    const card = document.createElement("span")
    card.className = "map-arrival-card"
    if (arrival.thumb) {
      const img = document.createElement("img")
      img.className = "map-arrival-thumb"
      img.src = arrival.thumb
      img.alt = ""
      card.append(img)
    }
    const body = document.createElement("span")
    body.className = "map-arrival-body"
    const price = document.createElement("span")
    price.className = `map-arrival-price${arrival.free ? " is-free" : ""}`
    price.textContent = arrival.price
    const title = document.createElement("span")
    title.className = "map-arrival-title"
    title.textContent = arrival.title
    body.append(price, title)
    card.append(body)
    if (arrival.distance) {
      const distance = document.createElement("span")
      distance.className = "map-arrival-distance"
      distance.textContent = arrival.distance
      card.append(distance)
    }
    const dot = document.createElement("span")
    dot.className = "map-arrival-dot"
    pin.append(card, dot)
    host.append(pin)
    return host
  }

  clearArrivalEmotes() {
    if (this.arrivalRadar) { this.arrivalRadar.remove(); this.arrivalRadar = null }
    if (this.arrivalPin) { this.arrivalPin.remove(); this.arrivalPin = null }
    if (this.arrivalInterval) { clearInterval(this.arrivalInterval); this.arrivalInterval = null }
    if (this.arrivalStopTimer) { clearTimeout(this.arrivalStopTimer); this.arrivalStopTimer = null }
    if (this.arrivalCleanupTimer) { clearTimeout(this.arrivalCleanupTimer); this.arrivalCleanupTimer = null }
    if (this.arrivalMarker) { try { this.arrivalMarker.remove() } catch (_e) { /* already gone */ } this.arrivalMarker = null }
  }

  disconnect() {
    if (this.boundTabShown) window.removeEventListener("native:tab-visibility", this.boundTabShown)
    // The permanent shell outlives the page: a panel left open must not keep the next page locked.
    unlockScroll("region-map:filter")
    unlockScroll("region-map:price")
    // Remove any in-flight arrival-emote burst so it can't linger on the persisted (keep-alive) map.
    this.clearArrivalEmotes()

    // Cancel a pending deferred zoom-change frame (see handleZoomChange) so it can't fire post-disconnect.
    if (this._zoomChangeRaf) { cancelAnimationFrame(this._zoomChangeRaf); this._zoomChangeRaf = null }

    // Abort any pending village requests
    if (this.villageLoadAbortController) {
      this.villageLoadAbortController.abort()
      this.villageLoadAbortController = null
    }

    // Clear village reload debounce timer
    if (this.villageReloadTimer) {
      clearTimeout(this.villageReloadTimer)
      this.villageReloadTimer = null
    }

    // Listing-marker cleanup
    if (this.listingMarkerAbortController) { this.listingMarkerAbortController.abort(); this.listingMarkerAbortController = null }
    if (this.listingMarkerReloadTimer) { clearTimeout(this.listingMarkerReloadTimer); this.listingMarkerReloadTimer = null }
    if (this.listingPopup) { this.listingPopup.remove(); this.listingPopup = null }

    this.mapTelemetry?.disconnect()
    if (this.boundPageHide) window.removeEventListener("pagehide", this.boundPageHide)

    // Save viewport for restoration on back-navigation
    if (this.map) {
      this.constructor._savedViewport = {
        center: this.map.getCenter().toArray(),
        zoom: this.map.getZoom(),
        pitch: this.map.getPitch(),
        bearing: this.map.getBearing()
      }
    }

    // Keep-alive: disconnect() fires on EVERY Turbo navigation (measured) — Turbo preserves the
    // #map-permanent node but Stimulus still re-instantiates the controller. So we detach THIS controller's
    // handlers (the next controller re-binds cleanly, no duplicates) but do NOT destroy the map / WebGL
    // context. The instance lives on the static field and is re-adopted by the next connect()→reEngageMap().
    // It is torn down only by a full page reload (JS reset) or when its container is gone (see connect()).
    if (this.map) {
      this.cleanupMapEventListeners()
      this.map = null
    }

    // Clean up district autocomplete listeners
    window.removeEventListener("district-autocomplete:select", this.handleDistrictAutocompleteSelect)
    window.removeEventListener("district-autocomplete:dropdown-shown", this.handleDropdownShown)

    // Cancel pending timers/animations so their callbacks never fire on a detached controller/element
    if (this.filterModalCloseTimer) { clearTimeout(this.filterModalCloseTimer); this.filterModalCloseTimer = null }
    if (this.filterModalOpenRaf) { cancelAnimationFrame(this.filterModalOpenRaf); this.filterModalOpenRaf = null }
    if (this.pricePanelCloseTimer) { clearTimeout(this.pricePanelCloseTimer); this.pricePanelCloseTimer = null }
    if (this.pricePanelOpenRaf) { cancelAnimationFrame(this.pricePanelOpenRaf); this.pricePanelOpenRaf = null }
    if (this.countTimer) { clearTimeout(this.countTimer); this.countTimer = null }
    if (this.countAbort) { this.countAbort.abort(); this.countAbort = null }
    if (this.villageReloadTimer) { clearTimeout(this.villageReloadTimer); this.villageReloadTimer = null }

    // Clear stored handler references
    this.boundEventHandlers = {}
  }

  /**
   * Clean up all map event listeners to prevent memory leaks
   */
  cleanupMapEventListeners() {
    if (!this.map) return

    // Remove map-level event listeners
    if (this.boundEventHandlers.zoomend) {
      this.map.off("zoomend", this.boundEventHandlers.zoomend)
    }
    if (this.boundEventHandlers.movestart) {
      this.map.off("movestart", this.boundEventHandlers.movestart)
    }
    if (this.boundEventHandlers.moveend) {
      this.map.off("moveend", this.boundEventHandlers.moveend)
    }
    if (this.boundEventHandlers.userZoomStart) {
      this.map.off("zoomstart", this.boundEventHandlers.userZoomStart)
    }
    if (this.boundEventHandlers.firstIdle) {
      this.map.off("idle", this.boundEventHandlers.firstIdle)
    }
    this._teardownDpr?.()

    // Remove instance-level handlers (keep-alive: the map survives, so these MUST be off()-ed or a re-adopt
    // would stack a duplicate set closing over a stale controller).
    if (this.boundEventHandlers.styleimagemissing) {
      this.map.off("styleimagemissing", this.boundEventHandlers.styleimagemissing)
    }
    if (this.boundEventHandlers.mapError) {
      this.map.off("error", this.boundEventHandlers.mapError)
    }
    if (this.boundEventHandlers.webglContextLost) {
      this.map.off("webglcontextlost", this.boundEventHandlers.webglContextLost)
    }

    // Remove district layer event listeners
    if (this.map.getLayer("districts-fill")) {
      if (this.boundEventHandlers.districtMouseenter) {
        this.map.off("mouseenter", "districts-fill", this.boundEventHandlers.districtMouseenter)
      }
      if (this.boundEventHandlers.districtMousemove) {
        this.map.off("mousemove", "districts-fill", this.boundEventHandlers.districtMousemove)
      }
      if (this.boundEventHandlers.districtMouseleave) {
        this.map.off("mouseleave", "districts-fill", this.boundEventHandlers.districtMouseleave)
      }
      if (this.boundEventHandlers.districtClick) {
        this.map.off("click", "districts-fill", this.boundEventHandlers.districtClick)
      }
    }

    // Remove village layer event listeners
    if (this.map.getLayer("villages-fill")) {
      if (this.boundEventHandlers.villageMouseenter) {
        this.map.off("mouseenter", "villages-fill", this.boundEventHandlers.villageMouseenter)
      }
      if (this.boundEventHandlers.villageMousemove) {
        this.map.off("mousemove", "villages-fill", this.boundEventHandlers.villageMousemove)
      }
      if (this.boundEventHandlers.villageMouseleave) {
        this.map.off("mouseleave", "villages-fill", this.boundEventHandlers.villageMouseleave)
      }
      if (this.boundEventHandlers.villageClick) {
        this.map.off("click", "villages-fill", this.boundEventHandlers.villageClick)
      }
    }

    // Remove listing-marker layer event listeners
    if (this.map.getLayer("listing-marker")) {
      if (this.boundEventHandlers.markerEnter) {
        this.map.off("mouseenter", "listing-marker", this.boundEventHandlers.markerEnter)
      }
      if (this.boundEventHandlers.markerLeave) {
        this.map.off("mouseleave", "listing-marker", this.boundEventHandlers.markerLeave)
      }
      if (this.boundEventHandlers.markerClick) {
        this.map.off("click", "listing-marker", this.boundEventHandlers.markerClick)
      }
    }
  }

  /**
   * Setup listener for district autocomplete selection
   */
  setupDistrictAutocompleteListener() {
    this.handleDistrictAutocompleteSelect = (event) => {
      this.mapTelemetry.send("map.search") // L3 behavioral beacon (opt-in only; gated inside)
      const { id, name, city, lat, lng } = event.detail

      // Fly to the district. This is a programmatic move (no originalEvent), so handleMoveStart will
      // not dismiss the sidebar opened below — no manual flag needed.
      this.map.flyTo({
        center: [lng, lat],
        zoom: this.villageZoomThreshold,
        duration: 1500
      })

      // Select district and load listings
      this.selectDistrictById(id, name, city)
    }

    window.addEventListener("district-autocomplete:select", this.handleDistrictAutocompleteSelect)
  }

  /**
   * Setup listener for district autocomplete dropdown shown
   * Hides sidebar when dropdown is shown to prevent overlap
   */
  setupDropdownShownListener() {
    this.handleDropdownShown = () => {
      if (this.hasSidebarTarget && this.sidebarExpanded) {
        this.hideSidebar()
      }
    }

    window.addEventListener("district-autocomplete:dropdown-shown", this.handleDropdownShown)
  }

  /**
   * Select a district by ID and load its listings
   */
  selectDistrictById(districtId, name, city) {
    this.selectedDistrictId = districtId
    this.selectedVillageId = null

    // Update polygon styling
    if (this.map.getLayer("districts-fill")) {
      this.map.setPaintProperty("districts-outline", "line-width", [
        "case",
        ["==", ["id"], districtId], 3,
        ["boolean", ["feature-state", "hover"], false], 2,
        0
      ])
      this.map.setPaintProperty("districts-fill", "fill-opacity", this.fillOpacityExpr(districtId))
    }

    // Load listings via Turbo Frame
    this.loadListingsForDistrict(districtId)

    // Show sidebar
    this.showSidebar(name, city)
  }

  /**
   * Initialize the self-hosted map (MapLibre + PMTiles, same-origin style).
   */
  async initializeMap() {
    if (!this.hasContainerTarget) {
      console.error("[RegionMap] Container target not found")
      return
    }

    // Defensive idempotency: never build a second map over a live one. connect()'s reuse path normally
    // prevents reaching here with a map set; this guards any other caller (and the async style-fetch gap).
    if (this.map) { this.map.resize(); return }

    // Defensive: this is the BUILD path (no reusable instance), so the persistent container must be empty.
    // Drop any orphaned <canvas> a previously-FAILED build left behind (e.g. transient WebGL context-loss),
    // otherwise — since disconnect() no longer removes the map for the keep-alive — dead canvases would
    // stack in the preserved #map-permanent across retries.
    this.containerTarget.querySelectorAll("canvas").forEach((c) => c.remove())

    // Self-hosted same-origin style (/map/style.json) — no key, no terrain to strip.
    const mapStyle = await this.engine.resolveStyle({ styleUrl: this.styleUrlValue })

    // Guard: element may have disconnected during async style fetch (Turbo navigation)
    if (!this.element.isConnected) return

    // Restore saved viewport on back-navigation (cache hit = same listing_type + stats version),
    // UNLESS an explicit center was deep-linked (e.g. from a detail-page thumbnail) — that must win.
    // Deep-linked center from the LIVE URL (?lat&lng&zoom) — e.g. a detail-page static-map thumbnail tap.
    // #map-permanent is data-turbo-permanent, so this.centerValue/explicitCenterValue are FROZEN at first
    // render (Taipei default when the first page wasn't a deep-link). The URL is the only fresh source on a
    // first /home BUILD, so build DIRECTLY at the deep-link — the map must never flash Taipei first.
    // (Was: built at the frozen center and the deep-link only spawned emotes → every platform saw the
    // map stuck on Taipei on the first /home visit from a Mapstatic tap.) A product arrival builds at the item.
    const arrival = this.productArrivalFromPage()
    const deepLink = arrival ? { center: arrival.item, zoom: arrival.zoom } : this.deepLinkCenterFromUrl()

    const cache = this.constructor._districtCache
    const saved = !deepLink && !this.explicitCenterValue && cache &&
      cache.listingType === this.listingTypeValue &&
      cache.version === this.statsVersionValue
      ? this.constructor._savedViewport
      : null

    // Initialize map — pitch 0° at start (Tier 1: Taiwan overview), transitions via handleZoomChange.
    // Common MapOptions valid on BOTH engines:
    const mapOptions = {
      container: this.containerTarget,
      style: mapStyle,
      center: deepLink?.center || saved?.center || this.centerValue,
      zoom: deepLink?.zoom || saved?.zoom || this.zoomValue,
      pitch: saved?.pitch || 0,
      bearing: saved?.bearing || 0,
      maxBounds: [[117.5, 21.0], [123.0, 26.5]], // Taiwan bounds with padding
      minZoom: 8,                  // Taiwan fills viewport at zoom 8
      maxZoom: 17,                 // Overzooming up to 17 (source maxzoom 14)
      // Render at the device's true pixel ratio (capped at 3 for perf) so SDF labels are crisp on hi-DPR
      // phones — deterministic + consistent with the dprResize handler below.
      pixelRatio: getPixelRatio(),
      // antialias stays false (MapLibre's default). This is NOT the cause/cure of the Chrome-DevTools text
      // softness we investigated (that's inherent SDF-vs-raster — see localIdeograph note below). It's kept
      // off to protect REAL iOS Safari users from the WebKit MSAA-discard bug (mapbox-gl-js #11609) that blurs
      // an idle map on iOS 15.4+. Cost: slightly less 3D-building edge smoothing — acceptable.
      antialias: false,
      // CJK 繁體中文 via on-device TinySDF (48px in v5.24 vs our 24px server SDF) — a marginally sharper Han
      // glyph (legibility). NB: this did NOT match the raster reference's crispness — the canvas is already
      // full DPR3 (console-measured), and SDF vector text is simply softer than a raster/DOM map. Latin stays
      // server-SDF. See LOCAL_CJK_FONT in map_engine.
      localIdeographFontFamily: LOCAL_CJK_FONT
    }

    // Vanilla MapLibre. Legal attributions are disclosed in Terms/Privacy instead of drawn over
    // the mobile map viewport. CJK Han glyphs render via on-device TinySDF 48px; Latin/digits use
    // the server SDF.
    this.map = new this.engine.mapClass({
      ...mapOptions,
      attributionControl: false
    })
    // Stash for the persistent-map keep-alive — re-adopted by the next connect() (see reEngageMap).
    mapKeepAlive.instance = this.map

    // Zoom buttons for mouse users only: touch screens pinch (the /home coach mark teaches it), and on a phone
    // the top-right corner belongs to the status bar and the filter button.
    if (window.matchMedia("(pointer: fine)").matches) {
      this.map.addControl(new this.engine.NavigationControl({ showCompass: false }), "top-right")
    }

    // First-build only: load district polygons once the style is ready. A re-adopt keeps the loaded layers,
    // so "load" must NOT carry the re-bindable interaction handlers — those live in attachCoreMapHandlers().
    this.map.on("load", () => {
      // Force resize after layout stabilizes to fix blurry text on mobile.
      // MapLibre skips the first ResizeObserver event, so we must trigger manually
      // after CSS has finished computing container dimensions.
      requestAnimationFrame(() => {
        this.map.resize()
        if (!this.containerTarget.clientHeight) console.warn("[RegionMap] map container has zero height")
      })

      this.loadDistrictPolygons().then(() => {
        this.handleZoomChange()
      }).catch(() => {
        // loadDistrictPolygons logs internally; ensure handleZoomChange still runs
        this.handleZoomChange()
      })

      // Deep-link arrival (full-load path), mirrors the re-adopt path: a product plays its radar + price pin,
      // any other deep link the emoji burst. Reuses the build-time values computed above.
      if (arrival) {
        this.showProductArrival(arrival)
      } else if (deepLink) {
        this.spawnArrivalEmotes(deepLink.center)
      }
    })

    // Instance-level handlers that must re-point at the live controller on every re-adopt.
    this.attachCoreMapHandlers()
  }

  // Bind the map-instance handlers that are NOT one-shot: glyph fallback (styleimagemissing), error logging,
  // viewport tier transitions (zoom/move), and DPR re-bake. Called on first build AND on every re-adopt so a
  // reused map's handlers always close over the CURRENT controller. cleanupMapEventListeners() removes them.
  attachCoreMapHandlers() {
    // Lazily create OUR generated pill/badge images the moment a layer needs them — the canonical race-proof
    // path (eager registration can lose to this handler). Everything else (basemap POI icons not in our
    // sprite) falls back to a 1x1 transparent pixel to suppress console errors.
    this.boundEventHandlers.styleimagemissing = ({ id }) => {
      if (this.map.hasImage(id)) return
      if (id === "sticker-bg") { this.createPriceStickerImage(); return }
      this.map.addImage(id, { width: 1, height: 1, data: new Uint8Array(4) })
    }
    this.map.on("styleimagemissing", this.boundEventHandlers.styleimagemissing)

    // Surface map-level failures instead of failing silently to a blank map (failed glyph/sprite/tile/GeoJSON
    // loads — e.g. self-hosted CJK SDF glyphs 404 → blank labels with NO other signal). Logged, not toasted.
    this.boundEventHandlers.mapError = (e) => {
      const err = e?.error?.message || e?.error || e?.message || "unknown map error"
      console.error("[RegionMap] map error:", err, e?.sourceId ? `(source: ${e.sourceId})` : "")
    }
    this.map.on("error", this.boundEventHandlers.mapError)

    // WebGL context loss — the GPU reclaimed our retained context (common on mobile WKWebView after the app
    // is backgrounded; the keep-alive holds the context session-long so it is exactly what the OS evicts).
    // The DOM-presence re-adopt guard can't see a dead context behind a surviving container, so invalidate
    // the stash here → the next /home navigation builds a clean map instead of re-adopting a zombie.
    this.boundEventHandlers.webglContextLost = () => {
      console.warn("[RegionMap] WebGL context lost — invalidating keep-alive stash for a clean rebuild")
      if (mapKeepAlive.instance === this.map) mapKeepAlive.instance = null
    }
    this.map.on("webglcontextlost", this.boundEventHandlers.webglContextLost)

    // Handle zoom changes for village layer loading + 3-tier pitch transitions.
    this.boundEventHandlers.zoomend = () => this.handleZoomChange()
    this.boundEventHandlers.movestart = (e) => this.handleMoveStart(e)
    this.boundEventHandlers.moveend = () => this.handleMoveEnd()
    this.map.on("zoomend", this.boundEventHandlers.zoomend)
    this.map.on("movestart", this.boundEventHandlers.movestart)
    this.map.on("moveend", this.boundEventHandlers.moveend)

    // Home coach mark: shows once the map has painted (data-map-ready / region-map:ready) and leaves the moment
    // the user zooms by hand (originalEvent = gesture, not a programmatic flyTo).
    this.boundEventHandlers.userZoomStart = (e) => {
      if (e.originalEvent) window.dispatchEvent(new CustomEvent("region-map:user-zoom"))
    }
    this.map.on("zoomstart", this.boundEventHandlers.userZoomStart)
    this.boundEventHandlers.firstIdle = () => {
      this.element.dataset.mapReady = "true"
      window.dispatchEvent(new CustomEvent("region-map:ready"))
    }
    this.map.once("idle", this.boundEventHandlers.firstIdle)

    // Re-apply DPR on change (page-zoom / display move) via the shared helper so region_map / static_map /
    // map_picker behave identically. onChange re-bakes the raster price pill at the new dpr to stay crisp.
    this._teardownDpr = setupDprResize(this.map, () => {
      if (this.map.hasImage("sticker-bg")) {
        this.map.removeImage("sticker-bg")
        this.createPriceStickerImage()
      }
    })
  }

  /**
   * Create the price-label pill background image.
   * A flat warm-cream pill (crumb-50 #FFFCF7) — hairline warm-neutral border + a single soft drop
   * shadow (NOT neumorphism). Cream rather than pure white so the pills belong to the warm map
   * family instead of reading as bright cool-white chips; charcoal price text stays AAA (13.46:1).
   * Reads as a clean map data-marker over any (now warm) choropleth colour.
   * Registered as a MapLibre stretchable image so icon-text-fit grows it to the price text.
   */
  createPriceStickerImage() {
    if (this.map.hasImage("sticker-bg")) return

    // Scale image to device DPR for sharp rendering on all screens
    const dpr = Math.min(Math.ceil(window.devicePixelRatio || 2), 3)
    const scale = dpr / 2  // relative to the 2x baseline
    const w = Math.round(80 * scale)
    const h = Math.round(44 * scale)
    const r = Math.round(10 * scale)  // pill radius
    const pad = Math.round(8 * scale) // breathing room so the drop shadow isn't clipped

    const canvas = document.createElement("canvas")
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext("2d")
    const bx = pad, by = pad, bw = w - pad * 2, bh = h - pad * 2

    // Single soft drop shadow — flat map-marker depth (down + slightly diffuse, no inner highlight)
    ctx.save()
    ctx.shadowColor = "rgba(45, 45, 45, 0.22)"
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = Math.round(1.5 * scale)
    ctx.shadowBlur = Math.round(4 * scale)
    ctx.fillStyle = "#FFFCF7"
    ctx.beginPath()
    ctx.roundRect(bx, by, bw, bh, r)
    ctx.fill()
    ctx.restore()

    // Crisp white surface (no shadow)
    ctx.fillStyle = "#FFFCF7"
    ctx.beginPath()
    ctx.roundRect(bx, by, bw, bh, r)
    ctx.fill()

    // Hairline border — subtle warm-neutral that ties to the cream basemap and defines the pill
    // edge on light (yellow-green) choropleth fills. Inset 0.5px for a crisp 1px line.
    ctx.lineWidth = Math.max(1, Math.round(1 * scale))
    ctx.strokeStyle = "#E6E0D4"
    ctx.beginPath()
    ctx.roundRect(bx + 0.5, by + 0.5, bw - 1, bh - 1, r)
    ctx.stroke()

    const imageData = ctx.getImageData(0, 0, w, h)

    this.map.addImage("sticker-bg", imageData, {
      pixelRatio: dpr,
      stretchX: [[pad + r, w - pad - r]],
      stretchY: [[pad + r, h - pad - r]],
      content: [pad, pad, w - pad, h - pad]
    })
  }

  /**
   * Load district GeoJSON and render choropleth polygons
   */
  async loadDistrictPolygons() {
    // Inline cache hit check: determines both loader visibility and data source
    const cache = this.constructor._districtCache
    const isCacheHit = cache?.listingType === this.listingTypeValue &&
                       cache?.version === this.statsVersionValue

    // Only show loader on cache miss (cache hit = instant render, e.g. back-navigation)
    if (!isCacheHit) this.showLoader()

    try {
      let geojson

      if (isCacheHit) {
        geojson = cache.geojson
      } else {
        const url = this.buildGeoJsonUrl()
        const response = await fetch(url)

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        geojson = await response.json()

        // LRU-1: replace previous listing_type cache entry
        this.constructor._districtCache = {
          listingType: this.listingTypeValue,
          version: this.statsVersionValue,
          geojson
        }
      }

      // Register the area-card pill background (once). styleimagemissing also lazily creates it,
      // so registration is race-proof either way.
      this.createPriceStickerImage()

      // Add GeoJSON source
      if (this.map.getSource("districts")) {
        this.map.getSource("districts").setData(geojson)
      } else {
        this.map.addSource("districts", { type: "geojson", data: geojson })
        this.addDistrictLayers()
      }
    } catch (error) {
      console.error("[RegionMap] Failed to load districts:", error)
    } finally {
      this.hideLoader()
    }
  }

  /**
   * Shared style config for the 區/里 area-card symbol layers (districts + villages). ONE card per area
   * (dabang pattern) carrying everything, so there is no second symbol layer to collide with: the
   * inventory count (orange, prominent) on top, the 里/區 name (grey) under it, and the price (charcoal)
   * below — placed at the polygon centroid. Count auto-merges/splits by tier (區 z<=13 -> 里 z14-15);
   * price moves to the building pills at max zoom. Only `id` and `source` differ between the two layers.
   */
  get areaCardLayerStyle() {
    return {
      filter: [">", ["get", "listings_count"], 0],
      layout: {
        "text-field": ["format",
          // Inventory count — the prominent orange headline (dabang-style)
          ["to-string", ["get", "listings_count"]], { "text-font": ["literal", ["Noto Sans Bold"]], "font-scale": 1.4, "text-color": "#FF8A1A" },
          "\n", {},
          // 里/區 name — quiet grey subtitle
          ["coalesce", ["get", "name"], ""], { "text-font": ["literal", ["Noto Sans Regular"]], "font-scale": 0.78, "text-color": "#8A8A8A" },
          "\n", {},
          // Price — charcoal value + grey unit suffix (range flows inline: line1 + line2 + suffix)
          ["coalesce", ["get", "price_line1"], ""], { "text-font": ["literal", ["Noto Sans Bold"]], "text-color": "#2D2D2D" },
          ["coalesce", ["get", "price_line2"], ""], { "text-font": ["literal", ["Noto Sans Bold"]], "text-color": "#2D2D2D" },
          ["coalesce", ["get", "price_suffix"], ""], { "text-font": ["literal", ["Noto Sans Bold"]], "font-scale": 0.85, "text-color": "#6B6B6B" }
        ],
        "text-font": ["Noto Sans Regular"],
        // Zoom-interpolated size: smaller cards when zoomed out (less clutter), fuller when zoomed in.
        "text-size": ["interpolate", ["linear"], ["zoom"], 9, 11, 13, 14, 16, 15],
        "text-line-height": 1.25,
        // Collision priority: the most significant areas (highest listings_count) win placement;
        // overlapping lower-priority cards are HIDDEN instead of piling up illegibly. Lower sort-key
        // renders first / wins collision, so negating listings_count gives high-count cards priority.
        "symbol-sort-key": ["-", ["get", "listings_count"]],
        "text-allow-overlap": false,
        "text-optional": false,
        "text-padding": 2,
        "icon-image": "sticker-bg",
        "icon-text-fit": "both",
        "icon-text-fit-padding": [5, 9, 5, 9],
        "icon-allow-overlap": false,
        "symbol-placement": "point"
      },
      paint: {
        // Per-section colours are set in text-field above; this is the default + halo for crisp glyphs.
        "text-color": "#2D2D2D",
        "text-halo-color": "#FFFFFF",
        "text-halo-width": 0.3,
        "icon-opacity": 1.0
      }
    }
  }

  /**
   * Choropleth fill-opacity expression — ONE scheme shared by districts AND villages so the data
   * layer is the visual focus from first paint and never JUMPS opacity between paint calls (the
   * prior code used different sets in add / select / click, and districts ≠ villages). The warm
   * brand ramp (PriceBuckets::COLORS) keeps its chroma at these alphas, where the old cool ramp
   * washed to grey. base → hover → selected from PriceBuckets.FILL_OPACITY* (the legend mixes its swatches to
   * the same base strength).
   * @param {number|null} selectedId feature id to render at the selected opacity (null = none)
   */
  fillOpacityExpr(selectedId) {
    const buckets = window.BusinessConstants?.RealEstate?.PriceBuckets || {}
    return [
      "case",
      // No-data regions stay FULLY TRANSPARENT — no grey NO_DATA wash dimming the bright basemap
      // in the resting (nothing-selected) state. Only regions that actually have listings get the
      // heat fill. (Was: every no-data district painted #CDC9C0 at 0.35 → a grey overlay everywhere.)
      ["==", ["get", "listings_count"], 0], 0,
      ["==", ["id"], selectedId ?? -1], buckets.FILL_OPACITY_SELECTED ?? 0.75,
      ["boolean", ["feature-state", "hover"], false], buckets.FILL_OPACITY_HOVER ?? 0.62,
      buckets.FILL_OPACITY ?? 0.5
    ]
  }

  /**
   * Add map layers for district polygons
   */
  addDistrictLayers() {
    // Choropleth fill layer
    this.map.addLayer({
      id: "districts-fill",
      type: "fill",
      source: "districts",
      paint: {
        "fill-color": this.getPriceColorExpression(),
        "fill-opacity": this.fillOpacityExpr(this.selectedDistrictId)
      }
    })

    // Outline layer
    this.map.addLayer({
      id: "districts-outline",
      type: "line",
      source: "districts",
      paint: {
        "line-color": "#FBF7EF",
        "line-width": [
          "case",
          ["==", ["id"], this.selectedDistrictId || -1], 3,
          ["boolean", ["feature-state", "hover"], false], 2,
          0
        ]
      }
    })

    // 區 area card (count + name + price)
    this.map.addLayer({
      id: "districts-price-sticker",
      type: "symbol",
      source: "districts",
      // Zoom-gating: keep district cards visible one zoom past the threshold so they never vanish in
      // the window where villages are still loading (village data loads only at zoom > threshold). The
      // brief threshold..threshold+1 overlap with village cards is resolved by the collision index.
      maxzoom: this.villageZoomThreshold + 1,
      ...this.areaCardLayerStyle
    })

    // Event listeners
    this.setupMapEventListeners()
  }

  /**
   * Setup map interaction event listeners
   * Stores handler references for cleanup on disconnect
   */
  setupMapEventListeners() {
    // Hover events - store bound handlers for cleanup
    this.boundEventHandlers.districtMouseenter = (e) => {
      this.map.getCanvas().style.cursor = "pointer"

      if (e.features.length > 0) {
        this.setHoverState(e.features[0].id, true)
      }
    }

    this.boundEventHandlers.districtMousemove = (e) => {
      if (e.features.length > 0 && e.features[0].id !== this.hoveredDistrictId) {
        this.setHoverState(this.hoveredDistrictId, false)
        this.setHoverState(e.features[0].id, true)
      }
    }

    this.boundEventHandlers.districtMouseleave = () => {
      this.map.getCanvas().style.cursor = ""
      this.setHoverState(this.hoveredDistrictId, false)
    }

    this.boundEventHandlers.districtClick = (e) => {
      if (e.features.length > 0) {
        this.handleDistrictClick(e.features[0])
      }
    }

    // Register district event listeners
    this.map.on("mouseenter", "districts-fill", this.boundEventHandlers.districtMouseenter)
    this.map.on("mousemove", "districts-fill", this.boundEventHandlers.districtMousemove)
    this.map.on("mouseleave", "districts-fill", this.boundEventHandlers.districtMouseleave)
    this.map.on("click", "districts-fill", this.boundEventHandlers.districtClick)
  }

  /**
   * Set hover feature state
   */
  setHoverState(districtId, hover) {
    if (districtId === null) return

    this.map.setFeatureState(
      { source: "districts", id: districtId },
      { hover }
    )
    this.hoveredDistrictId = hover ? districtId : null
  }

  /**
   * Generate MapLibre expression for price-based choropleth
   */
  getPriceColorExpression() {
    // Get thresholds from BusinessConstants (via window if available)
    const buckets = window.BusinessConstants?.RealEstate?.PriceBuckets || {}

    const thresholds = this.listingTypeValue === "rent"
      ? (buckets.RENT || [10000, 20000, 30000, 40000, 50000])
      : (buckets.SALE || [200000, 400000, 600000, 800000, 1000000])

    // Blue → Yellow → Orange "heat" scale (SSOT: BusinessConstants::RealEstate::PriceBuckets,
    // injected via window.BusinessConstants in the layout). This literal MUST mirror that SSOT — it
    // is only a safety net if the injection is absent, and must NOT diverge from the legend. Cold
    // (blue) = cheap → hot (orange) = expensive; NO brown.
    const colors = buckets.COLORS || ["#1F6FC5", "#79B8EA", "#FFE36E", "#FFB627", "#FF8A1A", "#FF5C0A"]
    const noDataColor = buckets.NO_DATA_COLOR || "#CDC9C0"

    // Rent uses median_price (total monthly rent), sale uses avg_price_ping (price per 坪)
    const priceField = this.listingTypeValue === "rent" ? "median_price" : "avg_price_ping"

    // Build step expression — 6 buckets (5 thresholds) matching the 6-row legend exactly.
    return [
      "case",
      ["==", ["get", "listings_count"], 0], noDataColor,
      ["==", ["get", priceField], 0], noDataColor, // too few listings to disclose a level

      ["<=", ["get", priceField], thresholds[0]], colors[0],
      ["<=", ["get", priceField], thresholds[1]], colors[1],
      ["<=", ["get", priceField], thresholds[2]], colors[2],
      ["<=", ["get", priceField], thresholds[3]], colors[3],
      ["<=", ["get", priceField], thresholds[4]], colors[4],
      colors[5]
    ]

  }

  /**
   * Handle district click - load listings in sidebar
   */
  handleDistrictClick(feature) {
    // At marker zoom the district choropleth is a faded backdrop — the 里 fill (and building markers)
    // are the selection targets. Ignore district-fill taps so they can't clobber the 里 filter.
    if (this.map.getZoom() >= this.markerZoomThreshold) return

    this.mapTelemetry.send("map.click") // L3 behavioral beacon (opt-in only; gated inside)
    const districtId = feature.id
    const { name, city } = feature.properties

    // Update selection state (clear village when district is clicked)
    this.selectedDistrictId = districtId
    this.selectedVillageId = null

    // Update polygon styling
    this.map.setPaintProperty("districts-outline", "line-width", [
      "case",
      ["==", ["id"], districtId], 3,
      ["boolean", ["feature-state", "hover"], false], 2,
      0
    ])
    this.map.setPaintProperty("districts-fill", "fill-opacity", this.fillOpacityExpr(districtId))

    // Load listings via Turbo Frame (with filters)
    this.loadListingsForDistrict(districtId)

    // Show sidebar
    this.showSidebar(name, city)

    // Dispatch custom event
    this.dispatch("district-selected", {
      detail: { districtId, name, city }
    })
  }

  /**
   * Show sidebar panel
   */
  showSidebar(districtName, cityName) {
    if (!this.hasSidebarTarget) return

    // Update district name in header
    const nameEl = this.sidebarTarget.querySelector("[data-district-name]")
    if (nameEl) {
      nameEl.textContent = `${cityName || ""} ${districtName}`.trim()
    }

    // Expand sidebar
    this.expandSidebar()
  }

  /**
   * Hide sidebar panel (action)
   */
  hideSidebar(clearSelection = true) {
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.add("translate-y-full", "xl:translate-x-full")
      this.sidebarTarget.style.transform = ""
    }
    this.hideSidebarBackdrop()
    // Implicit dismissal (user pan via handleMoveStart) passes false: keep the selection so the map
    // highlight and JS state stay in sync. Explicit close (close button / ESC / swipe-down) clears it.
    if (clearSelection) {
      this.selectedDistrictId = null
      this.selectedVillageId = null
    }
    this.sidebarExpanded = false
  }

  /**
   * Expand sidebar (swipe reveal or district selection)
   */
  expandSidebar() {
    if (!this.hasSidebarTarget) return

    this.sidebarExpanded = true
    this.showSidebarBackdrop()
    this.sidebarTarget.style.transform = ""
    this.sidebarTarget.classList.remove("translate-y-full", "xl:translate-x-full")
  }

  showSidebarBackdrop() {
    if (!this.hasSidebarBackdropTarget) return

    this.sidebarBackdropTarget.classList.remove("hidden")
  }

  hideSidebarBackdrop() {
    if (!this.hasSidebarBackdropTarget) return

    this.sidebarBackdropTarget.classList.add("hidden")
  }

  // ============================================================================
  // Bottom Sheet Swipe Gesture Handlers
  // ============================================================================

  /**
   * Handle swipe start on handle (collapsed state)
   */
  handleSwipeStart(event) {
    if (this.sidebarExpanded) return

    this.touchStartY = event.touches[0].clientY
    this.isDragging = true
    this.sidebarTarget.style.transition = "none"
  }

  /**
   * Handle swipe move - reveal bottom sheet progressively
   */
  handleSwipeMove(event) {
    if (!this.isDragging || this.touchStartY === null) return

    const currentY = event.touches[0].clientY
    const deltaY = this.touchStartY - currentY  // Positive = swiping up

    // Only allow upward swipe (reveal)
    if (deltaY > 0) {
      const maxTranslate = window.innerHeight * 0.7  // 70vh max
      const translateY = Math.min(deltaY, maxTranslate)
      this.sidebarTarget.style.transform = `translateY(calc(100% - ${translateY}px))`
    }
  }

  /**
   * Handle swipe end - snap to open or closed
   */
  handleSwipeEnd(event) {
    if (!this.isDragging) return

    this.isDragging = false
    this.sidebarTarget.style.transition = ""

    const endY = event.changedTouches[0].clientY
    const deltaY = this.touchStartY - endY

    // Threshold: 50px to trigger reveal
    const REVEAL_THRESHOLD = 50

    if (deltaY > REVEAL_THRESHOLD) {
      // Reveal sidebar
      this.expandSidebar()
    } else {
      // Snap back to hidden
      this.sidebarTarget.style.transform = ""
      this.sidebarTarget.classList.add("translate-y-full")
    }

    this.touchStartY = null
  }

  /**
   * Handle header drag start (expanded state - for drag-to-dismiss)
   */
  handleHeaderSwipeStart(event) {
    if (!this.sidebarExpanded) return

    this.touchStartY = event.touches[0].clientY
    this.isDragging = true
    this.sidebarTarget.style.transition = "none"
  }

  /**
   * Handle header drag move
   */
  handleHeaderSwipeMove(event) {
    if (!this.isDragging || !this.sidebarExpanded) return

    const currentY = event.touches[0].clientY
    const deltaY = currentY - this.touchStartY  // Positive = swiping down

    // Only allow downward drag (dismiss)
    if (deltaY > 0) {
      this.sidebarTarget.style.transform = `translateY(${deltaY}px)`
    }
  }

  /**
   * Handle header drag end
   */
  handleHeaderSwipeEnd(event) {
    if (!this.isDragging || !this.sidebarExpanded) return

    this.isDragging = false
    this.sidebarTarget.style.transition = ""

    const endY = event.changedTouches[0].clientY
    const deltaY = endY - this.touchStartY

    // Threshold: 100px to dismiss
    const DISMISS_THRESHOLD = 100

    if (deltaY > DISMISS_THRESHOLD) {
      this.hideSidebar()
    } else {
      // Snap back to expanded
      this.sidebarTarget.style.transform = ""
    }

    this.touchStartY = null
  }

  /**
   * Toggle listing type (action)
   */
  toggleListingType(event) {
    if (event.target.value === this.listingTypeValue) return
    this.applyListingType(event.target.value)
    // An open price panel follows the map into the new mode.
    if (this.hasPricePanelTarget && !this.pricePanelTarget.classList.contains("hidden") && this.selectedDistrictId) {
      this.openPricePanel()
    }
  }

  /**
   * The price panel's own 出租/出售 toggle (it reloads its frame itself): bring the map along.
   */
  syncListingTypeFromPanel({ params: { listingType } }) {
    if (!listingType || listingType === this.listingTypeValue) return
    this.listingTypeSelectTargets.forEach((radio) => { radio.checked = radio.value === listingType })
    this.applyListingType(listingType)
  }

  applyListingType(newType) {
    this.listingTypeValue = newType

    // Price scale differs ~1000x between rent (NT$/月) and sale (萬). A bound carried across the
    // switch would be nonsensical (e.g. a sale ceiling applied to a rent query), so clear price.
    if (this.filtersValue.price_min || this.filtersValue.price_max) {
      this.filtersValue = { ...this.filtersValue, price_min: null, price_max: null }
    }
    this.pendingFilters = { ...this.pendingFilters, price_min: null, price_max: null }

    // The filter sheet ships both preset sets; show this mode's and reset to 不限.
    if (this.hasFilterModalTarget) {
      this.filterModalTarget.querySelectorAll("[data-price-preset-group]").forEach((btn) => {
        btn.classList.toggle("hidden", btn.dataset.pricePresetGroup !== newType)
      })
      this.updatePriceDisplay()
    }

    // Update legend visibility
    if (this.hasRentLegendTarget && this.hasSaleLegendTarget) {
      this.rentLegendTarget.classList.toggle("hidden", newType !== "rent")
      this.saleLegendTarget.classList.toggle("hidden", newType !== "sale")
    }

    // Reload district data with new listing type
    this.loadDistrictPolygons().then(() => {
      // Update choropleth colors
      if (this.map.getLayer("districts-fill")) {
        this.map.setPaintProperty(
          "districts-fill",
          "fill-color",
          this.getPriceColorExpression()
        )
      }
    })

    // Update village choropleth and reload village data if loaded
    if (this.map.getLayer("villages-fill")) {
      this.map.setPaintProperty("villages-fill", "fill-color", this.getPriceColorExpression())
    }
    if (this.villageLayersLoaded) {
      this.loadVillagePolygons()
    }

    // Reload listings if district is selected (with filters)
    if (this.selectedDistrictId) {
      this.loadListingsForDistrict(this.selectedDistrictId)
    }

    // Update URL
    this.updateUrlWithFilters()
  }

  /**
   * Show loading indicator
   */
  showLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.remove("hidden")
    }
  }

  /**
   * Hide loading indicator
   */
  hideLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.add("hidden")
    }
  }

  /**
   * Fit map to Taiwan bounds (action)
   */
  fitTaiwan() {
    this.map.fitBounds(
      [[119.3, 21.8], [122.0, 25.3]],
      { padding: 20, duration: 1000 }
    )
  }

  /**
   * Go to user's location (action)
   */
  gotoMyLocation() {
    if (!navigator.geolocation) {
      console.warn("[RegionMap] Geolocation not supported")
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        this.map.flyTo({
          center: [longitude, latitude],
          zoom: 14,
          duration: 1500
        })
      },
      (error) => {
        console.warn("[RegionMap] Geolocation error:", error.message)
      }
    )
  }

  // ============================================================================
  // Filter Modal Actions
  // ============================================================================

  /**
   * Open filter modal (action)
   */
  openFilterModal() {
    if (!this.hasFilterModalTarget) return

    // Reset pending filters to current filters
    this.pendingFilters = { ...this.filtersValue }

    // Make the reopened sheet authoritative: re-derive ALL chips/pills/toggles/price from the
    // (re-seeded) committed state. Prior in-modal scratch edits (e.g. Reset without Apply) or
    // out-of-modal changes (inline bar, chip ×, clear-all) must not leave a stale selection that the
    // Apply button would then silently re-commit.
    this.syncAllFilterUI()
    this.refreshResultCount()

    // Show modal
    this.filterModalTarget.classList.remove("hidden")
    this.filterModalOpenRaf = requestAnimationFrame(() => {
      this.filterModalContentTarget.classList.remove("translate-y-full")
    })

    lockScroll("region-map:filter", { dismiss: () => this.closeFilterModal() })
  }

  /**
   * Close filter modal (action)
   */
  closeFilterModal() {
    if (!this.hasFilterModalTarget) return

    this.filterModalContentTarget.classList.add("translate-y-full")
    this.filterModalCloseTimer = setTimeout(() => {
      this.filterModalTarget.classList.add("hidden")
    }, 300)

    unlockScroll("region-map:filter")
  }

  /**
   * Open the region price-intelligence panel for the selected district (action). Loads the panel via
   * its Turbo Frame, then slides up the bottom sheet. No-op without a selected district.
   */
  openPricePanel() {
    if (!this.hasPricePanelTarget || !this.selectedDistrictId) return

    // Backdrop appears instantly (opening feedback). The content-sized sheet (max-h-[85vh], no fixed
    // height) slides in only AFTER its turbo-frame content is in place, so it animates at its FINAL
    // height. The old flow set frame.src then slid immediately, letting the async frame swap change the
    // height mid-slide — the sheet jumped from ~62px (empty frame) to ~85vh when data arrived. Same root
    // cause as the listings bottom sheet; here the delta is larger so load-then-slide is the right fix.
    this.pricePanelTarget.classList.remove("hidden")
    lockScroll("region-map:price", { dismiss: () => this.closePricePanel() })

    if (!this.hasPricePanelFrameTarget || !this.pricePanelUrlValue) {
      this.slidePricePanelIn()
      return
    }

    const url = this.pricePanelUrlValue.replace(":id", this.selectedDistrictId)
    const params = new URLSearchParams()
    params.set("listing_type", this.listingTypeValue)
    if (this.selectedVillageId) params.set("village_id", this.selectedVillageId)
    const target = `${url}?${params.toString()}`
    const frame = this.pricePanelFrameTarget

    // Same district already loaded → slide immediately (no refetch, no waiting).
    if (frame.hasAttribute("complete") && this.pricePanelLoadedSrc === target) {
      this.slidePricePanelIn()
      return
    }

    // Otherwise slide once the frame finishes loading; fallback so a slow/failed fetch never strands the
    // sheet off-screen behind the backdrop. A different district shows the skeleton, never stale numbers.
    if (this.pricePanelLoadedSrc && this.pricePanelLoadedSrc !== target && this.hasPricePanelSkeletonTarget) {
      frame.replaceChildren(this.pricePanelSkeletonTarget.content.cloneNode(true))
    }
    this.clearPricePanelOpenWatch()
    this.pricePanelSlideOnce = () => {
      this.clearPricePanelOpenWatch()
      this.pricePanelLoadedSrc = target
      this.slidePricePanelIn()
    }
    frame.addEventListener("turbo:frame-load", this.pricePanelSlideOnce, { once: true })
    this.pricePanelFallbackTimer = setTimeout(this.pricePanelSlideOnce, 600)
    frame.src = target
  }

  slidePricePanelIn() {
    this.pricePanelOpenRaf = requestAnimationFrame(() => {
      this.pricePanelContentTarget.classList.remove("translate-y-full")
    })
  }

  clearPricePanelOpenWatch() {
    if (this.pricePanelSlideOnce && this.hasPricePanelFrameTarget) {
      this.pricePanelFrameTarget.removeEventListener("turbo:frame-load", this.pricePanelSlideOnce)
    }
    this.pricePanelSlideOnce = null
    if (this.pricePanelFallbackTimer) {
      clearTimeout(this.pricePanelFallbackTimer)
      this.pricePanelFallbackTimer = null
    }
  }

  /**
   * Close the price-intelligence panel (action).
   */
  closePricePanel() {
    if (!this.hasPricePanelTarget) return

    // Cancel any pending open-watch / slide so a fast close-while-loading can't slide in after close.
    this.clearPricePanelOpenWatch()
    if (this.pricePanelOpenRaf) {
      cancelAnimationFrame(this.pricePanelOpenRaf)
      this.pricePanelOpenRaf = null
    }

    this.pricePanelContentTarget.classList.add("translate-y-full")
    this.pricePanelCloseTimer = setTimeout(() => {
      this.pricePanelTarget.classList.add("hidden")
    }, 300)
    unlockScroll("region-map:price")
  }

  /**
   * Toggle property type selection (action)
   */
  togglePropertyType(event) {
    const button = event.currentTarget
    const value = button.dataset.filterValue
    const isSelected = button.dataset.selected === "true"

    // Initialize array if needed
    if (!Array.isArray(this.pendingFilters.property_types)) {
      this.pendingFilters.property_types = []
    }

    if (value === "all") {
      // Clear all selections
      this.pendingFilters.property_types = []
      this.updatePropertyTypeButtons()
      this.refreshResultCount()
      return
    }

    if (isSelected) {
      // Remove from selection
      this.pendingFilters.property_types = this.pendingFilters.property_types.filter(t => t !== value)
    } else {
      // Add to selection
      this.pendingFilters.property_types.push(value)
    }

    this.updatePropertyTypeButtons()
    this.refreshResultCount()
  }

  /**
   * Update property type button states
   */
  updatePropertyTypeButtons() {
    const buttons = this.filterModalTarget.querySelectorAll("[data-filter-type='property_type']")
    const selectedTypes = this.pendingFilters.property_types || []

    buttons.forEach(button => {
      const value = button.dataset.filterValue
      const isAll = value === "all"
      const isSelected = isAll
        ? selectedTypes.length === 0
        : selectedTypes.includes(value)

      button.dataset.selected = isSelected.toString()
      button.setAttribute("aria-pressed", isSelected.toString())
    })
  }

  /**
   * Toggle a property type from the INLINE bar — instant-apply (commits to filtersValue and reloads
   * immediately, unlike the modal chips which stage into pendingFilters until Apply).
   */
  togglePropertyTypeInline(event) {
    const value = event.currentTarget.dataset.filterValue
    const types = Array.isArray(this.filtersValue.property_types) ? [...this.filtersValue.property_types] : []
    const idx = types.indexOf(value)
    if (idx >= 0) { types.splice(idx, 1) } else { types.push(value) }

    this.filtersValue = { ...this.filtersValue, property_types: types }
    this.pendingFilters = { ...this.pendingFilters, property_types: [...types] }
    this.syncPropertyTypeChips()
    this.reloadListingsAndUrl()
  }

  /**
   * Remove a single applied filter (action — the × on an applied-filter chip). Commits immediately.
   */
  removeFilterChip(event) {
    const type = event.currentTarget.dataset.filterType
    const value = event.currentTarget.dataset.filterValue
    const f = { ...this.filtersValue }

    if (type === "property_type") {
      f.property_types = (f.property_types || []).filter(t => t !== value)
    } else if (type === "price") {
      f.price_min = null
      f.price_max = null
    } else {
      f[type] = null // rooms / bathrooms / parking_type / pet_allowed / furnished / has_elevator
    }

    this.filtersValue = f
    this.pendingFilters = { ...f }
    this.syncAllFilterUI()
    this.reloadListingsAndUrl()
  }

  /**
   * Clear every active filter (action — 清除全部).
   */
  clearAllFilters() {
    this.filtersValue = {}
    this.pendingFilters = {}
    this.syncAllFilterUI()
    this.reloadListingsAndUrl()
  }

  /**
   * Sync every property-type chip (inline bar AND modal) to the committed selection — document-wide,
   * since the inline bar lives outside the modal subtree.
   */
  syncPropertyTypeChips() {
    const selected = this.filtersValue.property_types || []
    document.querySelectorAll("[data-filter-type='property_type']").forEach(btn => {
      const v = btn.dataset.filterValue
      const sel = v === "all" ? selected.length === 0 : selected.includes(v)
      btn.dataset.selected = sel.toString()
      btn.setAttribute("aria-pressed", sel.toString())
    })
  }

  /**
   * Re-sync all in-DOM filter UI (chips, modal pills/toggles, price) from the committed filters.
   */
  syncAllFilterUI() {
    this.syncPropertyTypeChips()
    if (this.hasFilterModalTarget) {
      this.updateFilterButtons("rooms")
      this.updateFilterButtons("bathrooms")
      this.updateFilterButtons("parking_type")
      this.updatePriceDisplay()
      if (this.hasPetAllowedCheckboxTarget) this.petAllowedCheckboxTarget.checked = this.pendingFilters.pet_allowed === "true"
      if (this.hasFurnishedCheckboxTarget) this.furnishedCheckboxTarget.checked = this.pendingFilters.furnished === "true"
      if (this.hasHasElevatorCheckboxTarget) this.hasElevatorCheckboxTarget.checked = this.pendingFilters.has_elevator === "true"
    }
  }

  /**
   * Commit-side reload: refresh the listings frame (which re-renders the applied-filter chips) and
   * the address bar. Shared by inline toggle, chip removal, and clear-all.
   */
  reloadListingsAndUrl() {
    if (this.selectedDistrictId) this.loadListingsForDistrict(this.selectedDistrictId)
    this.updateUrlWithFilters()
    this.updateFilterBadge()
  }

  /**
   * Active-filter count across the 6 dimensions (mirrors MapContainerComponent#active_filters_count;
   * features collapse to one).
   */
  activeFilterCount() {
    const f = this.filtersValue || {}
    let n = 0
    if (f.property_types?.length > 0) n++
    if (f.price_min || f.price_max) n++
    if (f.rooms) n++
    if (f.bathrooms) n++
    if (f.pet_allowed || f.furnished || f.has_elevator) n++
    if (f.parking_type) n++
    return n
  }

  /**
   * Keep the search-header badge + filter-button aria-label in sync with committed filters
   * (server renders them once; they'd otherwise go stale on every no-reload commit).
   */
  updateFilterBadge() {
    if (!this.hasFilterBadgeTarget) return

    const n = this.activeFilterCount()
    this.filterBadgeTarget.textContent = n
    this.filterBadgeTarget.classList.toggle("hidden", n === 0)

    if (this.hasFilterButtonTarget && this.filterLabelValue) {
      const suffix = n > 0 && this.filterCountTemplateValue
        ? `（${this.filterCountTemplateValue.replace("__N__", n)}）`
        : ""
      this.filterButtonTarget.setAttribute("aria-label", `${this.filterLabelValue}${suffix}`)
    }
  }

  /**
   * Select room filter (action)
   */
  selectRoomFilter(event) {
    const value = event.currentTarget.dataset.filterValue
    this.pendingFilters.rooms = value ? parseInt(value, 10) : null
    this.updateFilterButtons("rooms")
    this.refreshResultCount()
  }

  /**
   * Select bathroom filter (action)
   */
  selectBathroomFilter(event) {
    const value = event.currentTarget.dataset.filterValue
    this.pendingFilters.bathrooms = value ? parseInt(value, 10) : null
    this.updateFilterButtons("bathrooms")
    this.refreshResultCount()
  }

  /**
   * Toggle feature filter (action)
   */
  toggleFeatureFilter(event) {
    const filterType = event.currentTarget.dataset.filterType
    const isChecked = event.currentTarget.checked
    this.pendingFilters[filterType] = isChecked ? "true" : null
    this.refreshResultCount()
  }

  /**
   * Select parking filter (action)
   */
  selectParkingFilter(event) {
    const value = event.currentTarget.dataset.filterValue
    this.pendingFilters.parking_type = value || null
    this.updateFilterButtons("parking_type")
    this.refreshResultCount()
  }

  /**
   * Update filter button states for a specific filter type
   */
  updateFilterButtons(filterType) {
    const buttons = this.filterModalTarget.querySelectorAll(`[data-filter-type='${filterType}']`)
    const currentValue = this.pendingFilters[filterType]
    // rooms/bathrooms are integers; parking_type (and other value filters) are strings. parseInt on a
    // string value yields NaN, so the selected pill never matched — compare by the filter's native type.
    const numeric = filterType === "rooms" || filterType === "bathrooms"

    buttons.forEach(button => {
      const raw = button.dataset.filterValue
      const buttonValue = raw ? (numeric ? parseInt(raw, 10) : raw) : null
      const selected = (buttonValue === currentValue).toString()
      button.dataset.selected = selected
      button.setAttribute("aria-pressed", selected)
    })
  }

  /**
   * Update the price display line + preset highlighting from pendingFilters.
   * (The dual-range slider was removed — preset chips are the single price control.)
   */
  updatePriceDisplay() {
    if (this.hasPriceMinDisplayTarget) {
      this.priceMinDisplayTarget.textContent = this.formatPrice(this.pendingFilters.price_min)
    }
    if (this.hasPriceMaxDisplayTarget) {
      this.priceMaxDisplayTarget.textContent = this.formatPrice(this.pendingFilters.price_max)
    }
    this.updatePricePresetButtons()
  }

  /**
   * Highlight the active price preset chip (and the 不限 clear chip when no range is set).
   */
  updatePricePresetButtons() {
    if (!this.hasFilterModalTarget) return

    const curMin = this.normalizePriceBound(this.pendingFilters.price_min)
    const curMax = this.normalizePriceBound(this.pendingFilters.price_max)

    this.filterModalTarget.querySelectorAll("[data-filter-type='price_preset']").forEach(btn => {
      const active = this.normalizePriceBound(btn.dataset.priceMin) === curMin &&
                     this.normalizePriceBound(btn.dataset.priceMax) === curMax
      btn.dataset.selected = active.toString()
      btn.setAttribute("aria-pressed", active.toString())
    })

    // The 不限 (clear) chip is the lone data-filter-type='price' button.
    const unset = (curMin === null && curMax === null).toString()
    this.filterModalTarget.querySelectorAll("[data-filter-type='price']").forEach(btn => {
      btn.dataset.selected = unset
      btn.setAttribute("aria-pressed", unset)
    })
  }

  /** Normalize a price bound: blank / 0 / NaN → null, else integer (mirrors the server helper). */
  normalizePriceBound(value) {
    if (value === null || value === undefined || value === "") return null
    const n = parseInt(value, 10)
    return Number.isNaN(n) || n === 0 ? null : n
  }

  /**
   * Format price for display
   */
  formatPrice(value) {
    if (value === null || value === undefined) {
      return this.priceUnlimitedValue
    }

    // 萬 everywhere, like the legend and the area cards (rent keeps one decimal: 1.5萬/月)
    const wan = value / 10000
    if (this.listingTypeValue === "sale") return `${Math.round(wan)}${this.priceUnitWanValue}`

    return `${Number(wan.toFixed(1))}${this.priceUnitWanValue}${this.priceMonthlySuffixValue}`
  }

  /**
   * Apply price preset (action) — presets are the single price control.
   */
  applyPricePreset(event) {
    this.pendingFilters.price_min = this.normalizePriceBound(event.currentTarget.dataset.priceMin)
    this.pendingFilters.price_max = this.normalizePriceBound(event.currentTarget.dataset.priceMax)
    this.updatePriceDisplay()
    this.refreshResultCount()
  }

  /**
   * Clear the price range (action — the 不限 chip).
   */
  clearPrice() {
    this.pendingFilters.price_min = null
    this.pendingFilters.price_max = null
    this.updatePriceDisplay()
    this.refreshResultCount()
  }

  /**
   * Expand/collapse the 更多條件 (bathrooms/features/parking) tier.
   */
  toggleMoreFilters() {
    if (!this.hasMoreFiltersTarget) return
    const expanded = this.moreFiltersTarget.classList.toggle("hidden") === false
    if (this.hasMoreFiltersToggleTarget) {
      this.moreFiltersToggleTarget.setAttribute("aria-expanded", expanded.toString())
    }
    if (this.hasMoreFiltersChevronTarget) {
      this.moreFiltersChevronTarget.classList.toggle("rotate-180", expanded)
    }
  }

  /**
   * Reset all filters (action)
   */
  resetFilters() {
    this.pendingFilters = {}

    // Reset chip groups
    this.updatePropertyTypeButtons()
    this.updateFilterButtons("rooms")
    this.updateFilterButtons("bathrooms")
    this.updateFilterButtons("parking_type")

    // Reset price (presets + display)
    this.updatePriceDisplay()

    // Reset feature checkboxes
    if (this.hasPetAllowedCheckboxTarget) this.petAllowedCheckboxTarget.checked = false
    if (this.hasFurnishedCheckboxTarget) this.furnishedCheckboxTarget.checked = false
    if (this.hasHasElevatorCheckboxTarget) this.hasElevatorCheckboxTarget.checked = false

    this.refreshResultCount()
  }

  /**
   * Apply filters and reload data (action)
   */
  applyFilters() {
    // Update actual filters
    this.filtersValue = { ...this.pendingFilters }

    // Reflect the committed selection on the inline property-type bar (lives outside the modal).
    this.syncPropertyTypeChips()
    this.updateFilterBadge()

    // Close modal
    this.closeFilterModal()

    // Reload district polygons with new filters
    this.loadDistrictPolygons()

    // Reload listings if district is selected
    if (this.selectedDistrictId) {
      this.loadListingsForDistrict(this.selectedDistrictId)
    }

    // Update URL with filter params
    this.updateUrlWithFilters()
  }

  /**
   * Load listings for a specific district with current filters
   */
  loadListingsForDistrict(districtId) {
    const frame = document.querySelector("turbo-frame#listings-frame")
    if (!frame) return

    const url = this.buildListingsUrl(districtId)
    frame.src = url
  }

  /**
   * Serialize a filter set into URLSearchParams — the SINGLE source of truth for the listings URL,
   * the result-count URL, and the address-bar sync, so the three can never drift (village_id and the
   * scalar/array handling were previously duplicated and diverged).
   *
   * @param {URLSearchParams} params - target (may already carry unrelated query keys)
   * @param {Object} filters - the filter set to serialize (committed filtersValue OR pending)
   * @param {Object} opts
   * @param {boolean} opts.includeVillage - append the selected village_id (listings/count only)
   */
  serializeFilters(params, filters, { includeVillage = false } = {}) {
    const f = filters || {}
    params.set("listing_type", this.listingTypeValue)

    if (includeVillage && this.selectedVillageId) {
      params.set("village_id", this.selectedVillageId)
    }

    params.delete("property_types[]")
    if (f.property_types?.length > 0) {
      f.property_types.forEach(t => params.append("property_types[]", t))
    }

    const SCALARS = ["price_min", "price_max", "rooms", "bathrooms",
                     "pet_allowed", "furnished", "has_elevator", "parking_type"]
    SCALARS.forEach(key => {
      if (f[key]) { params.set(key, f[key]) } else { params.delete(key) }
    })

    return params
  }

  /**
   * Build listings URL from committed filters.
   */
  buildListingsUrl(districtId) {
    const base = this.listingsUrlValue.replace(":id", districtId)
    const params = this.serializeFilters(new URLSearchParams(), this.filtersValue, { includeVillage: true })
    return `${base}?${params.toString()}`
  }

  /**
   * Live result count for the Apply button — counts the PENDING (not-yet-applied) filters so the
   * user previews the commit. Per-district only; debounced + abortable; never blocks applying.
   */
  refreshResultCount() {
    if (!this.hasApplyCountTarget) return
    if (!this.selectedDistrictId) {
      this.applyCountTarget.textContent = this.applyLabelValue || this.applyCountTarget.textContent
      return
    }
    clearTimeout(this.countTimer)
    this.countTimer = setTimeout(() => this.fetchResultCount(), 250)
  }

  async fetchResultCount() {
    if (!this.hasApplyCountTarget || !this.selectedDistrictId) return
    if (this.countAbort) this.countAbort.abort()
    this.countAbort = new AbortController()

    const base = this.listingsUrlValue.replace(":id", this.selectedDistrictId)
    const params = this.serializeFilters(new URLSearchParams(), this.pendingFilters, { includeVillage: true })
    params.set("count_only", "1")

    try {
      const res = await fetch(`${base}?${params.toString()}`, {
        signal: this.countAbort.signal,
        headers: { Accept: "application/json" }
      })
      if (!res.ok) return
      const data = await res.json()
      if (typeof data.count === "number") {
        this.applyCountTarget.textContent = this.applyCountLabel(data.count)
      }
    } catch (e) {
      if (e.name !== "AbortError" && this.applyLabelValue) {
        this.applyCountTarget.textContent = this.applyLabelValue
      }
    }
  }

  /** Localized "顯示 N 筆" (falls back to the plain apply label if no template was provided). */
  applyCountLabel(count) {
    if (!this.applyCountTemplateValue) return this.applyLabelValue || `${count}`
    return this.applyCountTemplateValue.replace("__N__", count.toLocaleString())
  }

  /**
   * Build GeoJSON URL with filters
   */
  buildGeoJsonUrl() {
    const params = new URLSearchParams()
    params.set("listing_type", this.listingTypeValue)

    // Only listing_type + version for cache busting — minimal URL for HTTP cache efficiency.
    // Filter params removed: server ignores them, and unique URLs per filter combo
    // created duplicate HTTP cache entries for the same districts response.
    if (this.statsVersionValue) {
      params.set("v", this.statsVersionValue)
    }

    return `${this.districtsUrlValue}?${params.toString()}`
  }

  /**
   * Update URL with current committed filter state (without page reload). Uses the same
   * serializeFilters builder as the listings URL so the address bar can never drift from the fetch.
   */
  updateUrlWithFilters() {
    const params = this.serializeFilters(
      new URLSearchParams(window.location.search),
      this.filtersValue,
      { includeVillage: false }
    )
    const newUrl = `${window.location.pathname}?${params.toString()}`
    window.history.replaceState({}, "", newUrl)
  }

  // ============================================================================
  // Village Layer Management (Zoom-based loading)
  // ============================================================================

  /**
   * Handle zoom change: continuous pitch ramp + district/village layer toggling.
   *
   * Pitch eases in smoothly with zoom — pitch = clamp((zoom-11)*9, 0, MAX_AUTO_PITCH) — so the camera tilts
   * gradually into the 3D street view (~z16+) instead of snapping between tiers. Always north-up:
   * bearing is never auto-applied (user-initiated rotation only). Tiers are still computed, but
   * ONLY to drive village-layer loading, not the camera.
   *
   * Anti-jitter guards:
   * 1. Only runs on zoomend (not during continuous zoom)
   * 2. Pitch change ignored if delta < 5° (respects user manual adjustment)
   */
  // zoomend handler. CRITICAL: never mutate the camera (easeTo) or sources (addSource/setData/
  // setLayoutProperty) SYNCHRONOUSLY here. MapLibre dispatches zoomend mid event-fire/render/ease-stop,
  // so a synchronous easeTo or source mutation re-enters its camera/_render loop — the cause of
  // "TypeError: this._onEaseFrame is not a function" and "Attempting to run(), but is already running"
  // (seen when a user gesture interrupts the in-flight pitch ease). Coalesce all work to the next frame
  // so it runs on a clean tick, outside MapLibre's event/ease unwinding.
  handleZoomChange() {
    if (this._zoomChangeRaf) return // already scheduled — the pending frame will read the latest zoom
    this._zoomChangeRaf = requestAnimationFrame(() => {
      this._zoomChangeRaf = null
      if (this.map) this._applyZoomChange()
    })
  }

  /**
   * Deferred zoom-change work (runs on a clean frame via handleZoomChange): continuous pitch ramp +
   * district/village/marker tier toggling. Pitch = clamp((zoom-11)*9, 0, MAX_AUTO_PITCH), eased smoothly; always
   * north-up (bearing never auto-applied). Tiers drive village-layer loading only, not the camera.
   */
  _applyZoomChange() {
    const zoom = this.map.getZoom()

    // Tier tracking is retained ONLY for village layer toggling below (not for pitch/bearing).
    const currentTier = zoom <= 11 ? 1 : zoom <= this.villageZoomThreshold ? 2 : 3
    if (currentTier !== this.lastAppliedTier) this.lastAppliedTier = currentTier

    // Continuous pitch ramp. Guarded so a pitch ease never starts on top of another in-flight one
    // (_pitchEasing) and only when delta >= 5° (respects manual pitch). Deferred to this frame, so the
    // easeTo no longer runs inside the zoomend fire that broke MapLibre's ease lifecycle.
    const targetPitch = Math.max(0, Math.min((zoom - 11) * 9, MAX_AUTO_PITCH))
    if (!this._pitchEasing && Math.abs(this.map.getPitch() - targetPitch) >= 5) {
      this._pitchEasing = true
      this.map.easeTo({ pitch: targetPitch, duration: 400 })
      this.map.once("moveend", () => { this._pitchEasing = false })
    }

    // Village layer loading/unloading
    const threshold = this.villageZoomThreshold
    if (zoom > threshold && !this.villageLayersLoaded) {
      this.loadVillagePolygons()
    } else if (zoom <= threshold) {
      // Abort in-flight village fetch (race condition prevention)
      if (this.villageLoadAbortController) {
        this.villageLoadAbortController.abort()
        this.villageLoadAbortController = null
      }
      if (this.villageLayersLoaded) {
        this.removeVillageLayers()
      }
    }

    // Max-zoom tier: at marker threshold the choropleth heat fades and individual building/listing
    // price pins take over (gravex/zigbang pattern).
    if (zoom >= this.markerZoomThreshold) {
      if (!this.listingMarkersLoaded) this.loadListingMarkers()
      this.setChoroplethFaded(true)
    } else {
      if (this.listingMarkerAbortController) { this.listingMarkerAbortController.abort(); this.listingMarkerAbortController = null }
      if (this.listingMarkersLoaded) this.removeListingMarkers()
      this.setChoroplethFaded(false)
    }

    this.syncLegend(zoom)

    // R2 coalesces this with the pan callback; the disabled path keeps the legacy event type.
    this.mapTelemetry.scheduleViewport("map.zoom")
  }

  // First pan / zoom of this visit lets the waiting prompts start (lib/prompt_sequence). An area tap doesn't:
  // it opens the list, and a sheet over that list would bury what the user just asked for.
  noteInteraction() {
    if (this.interactionNoted) return
    this.interactionNoted = true
    window.dispatchEvent(new CustomEvent(MAP_INTERACTION_EVENT))
  }

  // Legend follows the tier: its scope reads 區 or 里, it steps aside at building-marker zoom (the choropleth is
  // a faded backdrop there), and it starts open where there is room for it.
  syncLegend(zoom) {
    const village = zoom > this.villageZoomThreshold
    const scopes = this.element.dataset
    this.element.querySelectorAll("[data-map-legend]").forEach((legend) => {
      legend.classList.toggle("is-faded", zoom >= this.markerZoomThreshold)
      const scope = legend.querySelector("[data-legend-scope]")
      if (scope) scope.textContent = village ? scopes.legendScopeVillage : scopes.legendScopeDistrict
      if (!this.legendOpened && window.matchMedia("(min-width: 1024px)").matches) legend.open = true
    })
    this.legendOpened = true
  }

  /**
   * Handle map move start - hide sidebar immediately for responsive UX
   * Skips hiding for programmatic moves (flyTo from dropdown selection)
   */
  handleMoveStart(event) {
    // Only a genuine USER gesture (drag / wheel-zoom / touch) should dismiss the sidebar. MapLibre
    // sets `originalEvent` ONLY on user-initiated camera moves; programmatic moves — the flyTo from a
    // region selection AND the pitch-ramp easeTo in handleZoomChange — have no originalEvent. Keying
    // on it (instead of a hand-managed boolean that desynced across overlapping async animations) is
    // the root-cause fix for the sidebar flickering shut right after a region/village is selected.
    if (!event || !event.originalEvent) return
    this.noteInteraction()

    if (this.hasSidebarTarget && this.sidebarExpanded) {
      this.hideSidebar(false) // keep selection on implicit pan dismissal (see hideSidebar)
    }
  }

  /**
   * Handle map move end - reload villages for new viewport with debounce + bounds dedup
   */
  handleMoveEnd() {
    const zoom = this.map.getZoom()
    const threshold = this.villageZoomThreshold

    // Only reload villages if we're at village zoom level
    if (zoom > threshold && this.villageLayersLoaded) {
      // 1) Bounds hash dedup: skip reload if viewport hasn't meaningfully changed
      const bounds = this.map.getBounds()
      const precision = zoom >= 16 ? 5 : zoom >= 14 ? 4 : 3
      const boundsHash = [
        bounds.getWest().toFixed(precision),
        bounds.getSouth().toFixed(precision),
        bounds.getEast().toFixed(precision),
        bounds.getNorth().toFixed(precision)
      ].join(",")

      if (boundsHash !== this.lastVillageBoundsHash) {
        this.lastVillageBoundsHash = boundsHash

        // 2) Debounce: 300ms after last move (rapid panning → single request)
        if (this.villageReloadTimer) clearTimeout(this.villageReloadTimer)
        this.villageReloadTimer = setTimeout(() => {
          // 3) AbortController handles in-flight request cancellation inside loadVillagePolygons
          this.loadVillagePolygons()
        }, 300)
      }
    }

    // Reload listing markers for the new viewport (same debounce + bounds-dedup as villages).
    if (zoom >= this.markerZoomThreshold && this.listingMarkersLoaded) {
      const b = this.map.getBounds()
      const hash = [ b.getWest(), b.getSouth(), b.getEast(), b.getNorth() ].map((v) => v.toFixed(4)).join(",")
      if (hash !== this.lastMarkerBoundsHash) {
        this.lastMarkerBoundsHash = hash
        if (this.listingMarkerReloadTimer) clearTimeout(this.listingMarkerReloadTimer)
        this.listingMarkerReloadTimer = setTimeout(() => this.loadListingMarkers(), 300)
      }
    }

    // Dispatch event for other controllers (e.g., district-autocomplete)
    this.dispatch("moveend")

    // R2 coalesces this with the zoom callback; the disabled path keeps the legacy event type.
    this.mapTelemetry.scheduleViewport("map.pan")
  }

  buildMapBeaconEvent(eventType, clientSequence) {
    if (!this.l3ConsentValue || !this.beaconUrlValue || !this.map) return null

    const center = this.map.getCenter()
    return {
      event_type: eventType,
      lat: center.lat,
      lng: center.lng,
      zoom: this.map.getZoom(),
      vertical: "realestate",
      listing_type: this.listingTypeValue,
      property_types: Array.isArray(this.filtersValue?.property_types) ? this.filtersValue.property_types : [],
      event_at: new Date().toISOString(),
      idempotency_key: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
      client_sequence: clientSequence
    }
  }

  /**
   * Load village polygons for current viewport
   */
  async loadVillagePolygons() {
    if (!this.villagesUrlValue) {
      console.warn("[RegionMap] Villages URL not configured")
      return
    }

    // Abort any pending village request
    if (this.villageLoadAbortController) {
      this.villageLoadAbortController.abort()
    }
    this.villageLoadAbortController = new AbortController()

    try {
      const bounds = this.map.getBounds()
      const url = this.buildVillagesGeoJsonUrl(bounds)

      const response = await fetch(url, {
        signal: this.villageLoadAbortController.signal
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const geojson = await response.json()

      // Add or update villages source
      if (this.map.getSource("villages")) {
        // Feature-state is cleared by setData; reset JS hover tracking to match (avoid stale id).
        this.hoveredVillageId = null
        this.map.getSource("villages").setData(geojson)
      } else {
        // Hide district layers BEFORE adding village layers to prevent overlap
        this.setDistrictLayersVisibility("none")
        this.map.addSource("villages", { type: "geojson", data: geojson })
        this.addVillageLayers()
      }

      this.setDistrictLayersVisibility("none")

      // Post-fetch zoom verification: discard if user zoomed out during async load
      const currentZoom = this.map.getZoom()
      const currentThreshold = this.villageZoomThreshold
      if (currentZoom <= currentThreshold) {
        this.removeVillageLayers()
        return
      }
      this.villageLayersLoaded = true

      // Villages load async; if we're already at building-pill zoom (e.g. a Turbo back-nav restored a
      // z>=16 viewport), the synchronous setChoroplethFaded already ran before these layers existed.
      // Re-apply it now so the 里 price stickers/fill stay a faded backdrop under the building pills.
      if (currentZoom >= this.markerZoomThreshold) this.setChoroplethFaded(true)

    } catch (error) {
      if (error.name === "AbortError") {
        // Request was aborted, ignore
        return
      }
      console.error("[RegionMap] Failed to load villages:", error)
    }
  }

  /**
   * Add map layers for village polygons
   */
  addVillageLayers() {
    // Choropleth fill layer (same style as districts)
    this.map.addLayer({
      id: "villages-fill",
      type: "fill",
      source: "villages",
      paint: {
        "fill-color": this.getPriceColorExpression(),
        "fill-opacity": this.fillOpacityExpr(this.selectedVillageId)
      }
    })

    // Outline layer
    this.map.addLayer({
      id: "villages-outline",
      type: "line",
      source: "villages",
      paint: {
        "line-color": "#FBF7EF",
        "line-width": [
          "case",
          ["boolean", ["feature-state", "hover"], false], 2,
          0
        ]
      }
    })

    // 里 area card (count + name + price)
    this.map.addLayer({
      id: "villages-price-sticker",
      type: "symbol",
      source: "villages",
      // Zoom-gating: villages appear only from the village zoom threshold up (districts hide here).
      minzoom: this.villageZoomThreshold,
      ...this.areaCardLayerStyle
    })

    // Setup village event listeners
    this.setupVillageEventListeners()
  }

  /**
   * Setup event listeners for village layer
   * Stores handler references for cleanup on disconnect
   */
  setupVillageEventListeners() {
    // Hover events - store bound handlers for cleanup
    this.boundEventHandlers.villageMouseenter = (e) => {
      this.map.getCanvas().style.cursor = "pointer"

      if (e.features.length > 0) {
        this.setVillageHoverState(e.features[0].id, true)
      }
    }

    this.boundEventHandlers.villageMousemove = (e) => {
      if (e.features.length > 0 && e.features[0].id !== this.hoveredVillageId) {
        this.setVillageHoverState(this.hoveredVillageId, false)
        this.setVillageHoverState(e.features[0].id, true)
      }
    }

    this.boundEventHandlers.villageMouseleave = () => {
      this.map.getCanvas().style.cursor = ""
      this.setVillageHoverState(this.hoveredVillageId, false)
    }

    this.boundEventHandlers.villageClick = (e) => {
      // At marker zoom the faded 里 fill and the building pills overlap. A building pill under the
      // tap wins (finer per-building selection); a bare 里 tap filters the list to that 里's area.
      if (this.map.getLayer("listing-marker") &&
          this.map.queryRenderedFeatures(e.point, { layers: [ "listing-marker" ] }).length) return
      if (e.features.length > 0) {
        this.handleVillageClick(e.features[0])
      }
    }

    // Register village event listeners
    this.map.on("mouseenter", "villages-fill", this.boundEventHandlers.villageMouseenter)
    this.map.on("mousemove", "villages-fill", this.boundEventHandlers.villageMousemove)
    this.map.on("mouseleave", "villages-fill", this.boundEventHandlers.villageMouseleave)
    this.map.on("click", "villages-fill", this.boundEventHandlers.villageClick)
  }

  /**
   * Set hover feature state for village
   */
  setVillageHoverState(villageId, hover) {
    if (villageId === null || !this.map.getSource("villages")) return

    this.map.setFeatureState(
      { source: "villages", id: villageId },
      { hover }
    )
    this.hoveredVillageId = hover ? villageId : null
  }

  /**
   * Handle village click - load parent district listings
   * Village-level listing filtering requires GPS-based spatial query (future enhancement)
   */
  handleVillageClick(feature) {
    // A 里 tap filters the sidebar list to THAT 里 (village_id). Building markers (which sit on top and
    // win the tap — see the click closure) handle finer per-building selection.
    this.mapTelemetry.send("map.click") // L3 behavioral beacon (opt-in only; gated inside)
    const { name, district_id, district_name, city } = feature.properties

    // Update selection state
    this.selectedVillageId = feature.id
    this.selectedDistrictId = district_id

    // Outline the selected 里 (kept even at marker zoom — shows which area the list is filtered to).
    this.map.setPaintProperty("villages-outline", "line-width", [
      "case",
      ["==", ["id"], feature.id], 3,
      ["boolean", ["feature-state", "hover"], false], 2,
      0
    ])
    // Repaint the fill only below marker zoom; at marker zoom the choropleth stays a faded backdrop
    // (setChoroplethFaded owns it) so the building markers stay visible over the selected 里.
    if (this.map.getZoom() < this.markerZoomThreshold) {
      this.map.setPaintProperty("villages-fill", "fill-opacity", this.fillOpacityExpr(feature.id))
    }

    // Load listings for parent district
    if (district_id) {
      this.loadListingsForDistrict(district_id)
    }

    // Show sidebar with village name and parent district context
    const subtitle = `${city || ""} ${district_name || ""}`.trim()
    this.showSidebar(name, subtitle)

    // Dispatch custom event
    this.dispatch("village-selected", {
      detail: { villageId: feature.id, districtId: district_id, name, district_name, city }
    })
  }

  /**
   * Remove village layers from map
   */
  removeVillageLayers() {
    // Unregister village event handlers BEFORE teardown. addVillageLayers → setupVillageEventListeners
    // re-binds NEW closures on every re-entry; without this off(), a zoom-threshold round-trip
    // (z>13 → z<=13 → z>13) accumulates handlers → village click fires N times → duplicate sidebar loads.
    if (this.map.getLayer("villages-fill")) {
      if (this.boundEventHandlers.villageMouseenter) this.map.off("mouseenter", "villages-fill", this.boundEventHandlers.villageMouseenter)
      if (this.boundEventHandlers.villageMousemove) this.map.off("mousemove", "villages-fill", this.boundEventHandlers.villageMousemove)
      if (this.boundEventHandlers.villageMouseleave) this.map.off("mouseleave", "villages-fill", this.boundEventHandlers.villageMouseleave)
      if (this.boundEventHandlers.villageClick) this.map.off("click", "villages-fill", this.boundEventHandlers.villageClick)
    }

    if (this.map.getLayer("villages-price-sticker")) {
      this.map.removeLayer("villages-price-sticker")
    }
    if (this.map.getLayer("villages-fill")) {
      this.map.removeLayer("villages-fill")
    }
    if (this.map.getLayer("villages-outline")) {
      this.map.removeLayer("villages-outline")
    }
    if (this.map.getSource("villages")) {
      this.map.removeSource("villages")
    }

    // Show district layers again
    this.setDistrictLayersVisibility("visible")
    this.villageLayersLoaded = false
    this.hoveredVillageId = null
    this.lastVillageBoundsHash = null
  }

  /**
   * Set visibility of district layers
   */
  setDistrictLayersVisibility(visibility) {
    if (this.map.getLayer("districts-fill")) {
      this.map.setLayoutProperty("districts-fill", "visibility", visibility)
    }
    if (this.map.getLayer("districts-outline")) {
      this.map.setLayoutProperty("districts-outline", "visibility", visibility)
    }
    if (this.map.getLayer("districts-price-sticker")) {
      this.map.setLayoutProperty("districts-price-sticker", "visibility", visibility)
    }
  }

  /**
   * Build villages GeoJSON URL with viewport bounds
   */
  buildVillagesGeoJsonUrl(bounds) {
    const params = new URLSearchParams()

    // Viewport bounds (grid-snapped for HTTP cache sharing)
    this.setViewportBoundsParams(params, bounds)

    // Listing type
    params.set("listing_type", this.listingTypeValue)

    const villageVersion = this.villagesStatsVersionValue || this.statsVersionValue
    if (villageVersion) {
      params.set("v", villageVersion)
    }

    return `${this.villagesUrlValue}?${params.toString()}`
  }

  // ===================== Listing markers (max zoom: per-building price pins) =====================

  buildListingsGeojsonUrl(bounds) {
    const params = new URLSearchParams()
    // Listing markers use the RAW viewport bbox (NOT the outward grid-snap used for villages). The server
    // caps results by RECENCY (geojson#listings → .recent.limit(500)), so an OUTWARD-snapped (enlarged)
    // bbox in a dense area would fill the cap with newest-but-marginal listings and silently DROP older
    // listings that are actually on screen. Raw bbox keeps the capped set == the visible viewport.
    params.set("min_lat", bounds.getSouth())
    params.set("max_lat", bounds.getNorth())
    params.set("min_lng", bounds.getWest())
    params.set("max_lng", bounds.getEast())
    params.set("listing_type", this.listingTypeValue)
    return `${this.listingsGeojsonUrlValue}?${params.toString()}`
  }

  // Snap the viewport bbox OUTWARD to a fixed grid (floor min / ceil max) and write it into params, so
  // adjacent viewports in the same grid cell produce the SAME URL (the public HTTP + server cache hit)
  // while the snapped box still fully covers the visible viewport. Used by the villages choropleth (large
  // polygons, cap rarely hit) — NOT by listings markers (see buildListingsGeojsonUrl for why).
  setViewportBoundsParams(params, bounds) {
    const g = VIEWPORT_BBOX_GRID_DEG
    const floor = (v) => (Math.floor(v / g) * g).toFixed(VIEWPORT_BBOX_DECIMALS)
    const ceil = (v) => (Math.ceil(v / g) * g).toFixed(VIEWPORT_BBOX_DECIMALS)
    params.set("min_lat", floor(bounds.getSouth()))
    params.set("max_lat", ceil(bounds.getNorth()))
    params.set("min_lng", floor(bounds.getWest()))
    params.set("max_lng", ceil(bounds.getEast()))
  }

  async loadListingMarkers() {
    if (!this.listingsGeojsonUrlValue) return
    if (this.listingMarkerAbortController) this.listingMarkerAbortController.abort()
    this.listingMarkerAbortController = new AbortController()

    try {
      const url = this.buildListingsGeojsonUrl(this.map.getBounds())
      const res = await fetch(url, { signal: this.listingMarkerAbortController.signal })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const geojson = await res.json()

      if (this.map.getSource("listing-markers")) {
        this.map.getSource("listing-markers").setData(geojson)
      } else {
        this.map.addSource("listing-markers", {
          type: "geojson", data: geojson
        })
        this.addListingMarkerLayers()
      }

      // Discard if the user zoomed back out during the async fetch.
      if (this.map.getZoom() < this.markerZoomThreshold) { this.removeListingMarkers(); return }
      this.listingMarkersLoaded = true
    } catch (e) {
      if (e.name !== "AbortError") console.error("[RegionMap] Failed to load listing markers:", e)
    }
  }

  addListingMarkerLayers() {
    // One pill per building/社區 (no clustering — area counts are the 區/里 bubbles at lower zoom).
    this.map.addLayer({
      id: "listing-marker", type: "symbol", source: "listing-markers",
      layout: {
        // Entry price; a building/社區 with N listings appends the count unit (件).
        "text-field": ["case",
          [">", ["get", "count"], 1],
          ["concat", ["get", "price_label"], "  ", ["to-string", ["get", "count"]], this.unitCountValue || "件"],
          ["get", "price_label"]
        ],
        "text-font": ["Noto Sans Bold"],
        "text-size": 13,
        "icon-image": "sticker-bg",
        "icon-text-fit": "both",
        "icon-text-fit-padding": [4, 8, 4, 8],
        "text-allow-overlap": false,
        "icon-allow-overlap": false,
        "symbol-sort-key": ["-", ["get", "price"]]
      },
      paint: { "text-color": "#2D2D2D", "text-halo-color": "#FFFFFF", "text-halo-width": 0.3 }
    })
    this.setupListingMarkerListeners()
  }

  setupListingMarkerListeners() {
    this.boundEventHandlers.markerEnter = () => { this.map.getCanvas().style.cursor = "pointer" }
    this.boundEventHandlers.markerLeave = () => { this.map.getCanvas().style.cursor = "" }
    this.boundEventHandlers.markerClick = (e) => { if (e.features && e.features.length) this.handleListingMarkerClick(e.features[0]) }
    this.map.on("mouseenter", "listing-marker", this.boundEventHandlers.markerEnter)
    this.map.on("mouseleave", "listing-marker", this.boundEventHandlers.markerLeave)
    this.map.on("click", "listing-marker", this.boundEventHandlers.markerClick)
  }

  // Building/社區 marker tap → in-map card. count==1 → single listing; count>1 → 棟別 list. Popup is
  // the self-hosted MapLibre card; navigate as fallback when no Popup class (legacy MapTiler).
  handleListingMarkerClick(feature) {
    this.mapTelemetry.send("map.click")
    const p = feature.properties || {}
    const count = Number(p.count || 1)
    const coords = feature.geometry.coordinates

    // Center + a one-step zoom toward the tapped building (capped at maxzoom) before listing it.
    const targetZoom = Math.min(this.map.getZoom() + 1, this.map.getMaxZoom())
    this.map.easeTo({ center: coords, zoom: targetZoom })

    if (count > 1) {
      let items = []
      try { items = typeof p.listings === "string" ? JSON.parse(p.listings) : (p.listings || []) } catch (_e) { items = [] }
      this.showListingListPopup(coords, items)
    } else {
      this.showSingleListingPopup(coords, p)
    }

    this.dispatch("listing-selected", { detail: { listingId: feature.id, count } })
  }

  popupClass() {
    return this.engine && this.engine.maplibregl && this.engine.maplibregl.Popup
  }

  // One listing → compact card. (No Popup engine → navigate.)
  showSingleListingPopup(lngLat, p) {
    const Popup = this.popupClass()
    if (!Popup) {
      if (p.url) { window.Turbo && window.Turbo.visit ? window.Turbo.visit(p.url) : (window.location.href = p.url) }
      return
    }
    const ping = p.area_ping ? `${p.area_ping} ${this.unitPingValue || "坪"}` : ""
    const html = `<div class="text-sm">
      <div class="font-semibold text-charcoal-800">${this.escapeHtml(p.title)}</div>
      <div class="text-crust-600 font-bold mt-1">${this.escapeHtml(p.price_label)}</div>
      ${ping ? `<div class="text-xs text-charcoal-500 mt-0.5">${this.escapeHtml(ping)}</div>` : ""}
      <a href="${this.escapeHtml(p.url)}" data-turbo-frame="_top" class="text-xs text-crust-600 underline mt-1 inline-block">${this.escapeHtml(this.viewDetailLabelValue || "查看詳情")}</a>
    </div>`
    if (this.listingPopup) this.listingPopup.remove()
    this.listingPopup = new Popup({ offset: 16, closeButton: true, maxWidth: "260px" })
      .setLngLat(lngLat).setHTML(html).addTo(this.map)
  }

  // N listings in one building/社區 group → scrollable 매물리스트. items: [{title, price_label, url}]
  showListingListPopup(lngLat, items) {
    const Popup = this.popupClass()
    if (!Popup || !items || !items.length) return
    const rows = items.map((l) => `<a href="${this.escapeHtml(l.url)}" data-turbo-frame="_top" class="flex items-center justify-between gap-3 py-1.5 border-t border-line-warm first:border-0">
        <span class="truncate text-charcoal-700">${this.escapeHtml(l.title)}</span>
        <span class="shrink-0 font-bold text-crust-600">${this.escapeHtml(l.price_label)}</span>
      </a>`).join("")
    const html = `<div class="text-sm">
      <div class="font-semibold text-charcoal-800 mb-1">${items.length}${this.unitCountValue || "件"}</div>
      <div class="max-h-64 overflow-y-auto -mr-1 pr-1">${rows}</div>
    </div>`
    if (this.listingPopup) this.listingPopup.remove()
    this.listingPopup = new Popup({ offset: 16, closeButton: true, maxWidth: "300px" })
      .setLngLat(lngLat).setHTML(html).addTo(this.map)
  }

  escapeHtml(str) {
    return String(str == null ? "" : str).replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ))
  }

  removeListingMarkers() {
    if (this.boundEventHandlers.markerClick && this.map.getLayer("listing-marker")) {
      this.map.off("click", "listing-marker", this.boundEventHandlers.markerClick)
      this.map.off("mouseenter", "listing-marker", this.boundEventHandlers.markerEnter)
      this.map.off("mouseleave", "listing-marker", this.boundEventHandlers.markerLeave)
    }
    if (this.map.getLayer("listing-marker")) this.map.removeLayer("listing-marker")
    if (this.map.getSource("listing-markers")) this.map.removeSource("listing-markers")
    if (this.listingPopup) { this.listingPopup.remove(); this.listingPopup = null }
    this.listingMarkersLoaded = false
    this.lastMarkerBoundsHash = null
  }

  // Fade the choropleth heat (+ hide its price stickers) so markers are the focus at max zoom.
  setChoroplethFaded(faded) {
    [ [ "villages-fill", this.selectedVillageId ], [ "districts-fill", this.selectedDistrictId ] ].forEach(([ id, selId ]) => {
      if (this.map.getLayer(id)) {
        this.map.setPaintProperty(id, "fill-opacity", faded ? this.choroplethFadeOpacity : this.fillOpacityExpr(selId))
      }
    })
    ;[ "villages-price-sticker", "districts-price-sticker" ].forEach((id) => {
      if (this.map.getLayer(id)) this.map.setLayoutProperty(id, "visibility", faded ? "none" : "visible")
    })
  }
}
